// API client for TWG Gen3 backend

import { auth } from './firebase';
import { useBranchStore } from '../stores/branchStore';
import { useAuthStore } from '../stores/authStore';
import { getImpersonationToken } from '../stores/impersonationStore';
import type { BranchLocation, BranchAddress, Branch } from '@twg/shared';

// API_BASE with defensive trim() to prevent env var whitespace corruption.
// Falls back to localhost:3001 for local development without env var setup.
// The trim() prevents production bugs from Docker/k8s configs with trailing newlines.
const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:3001').trim();

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
  };
  meta?: Record<string, unknown>;
  /** The request worked, but the person should know something (for example a hand-placed pin that could not be locked). */
  warnings?: Array<{ code: string; message: string }>;
}

/**
 * Get the current user's Firebase ID token for API authentication.
 *
 * Uses authStore's firebaseUser for consistency with app auth state.
 * This ensures API calls only have tokens when isAuthenticated is true,
 * preventing race conditions where Firebase SDK's auth.currentUser is
 * out of sync with the app's auth state.
 *
 * @param forceRefresh - Force refresh the token even if not expired
 */
async function getAuthToken(forceRefresh = false): Promise<string | null> {
  // First try authStore's firebaseUser for consistency with isAuthenticated state
  const storeUser = useAuthStore.getState().firebaseUser;
  // Fallback to Firebase SDK's currentUser (for edge cases during auth initialization)
  const user = storeUser ?? auth.currentUser;

  if (!user) {
    // Only log in development to help debug auth issues
    if (import.meta.env.DEV) {
      console.debug('getAuthToken: No user available (store or Firebase SDK)');
    }
    return null;
  }

  try {
    return await user.getIdToken(forceRefresh);
  } catch (error) {
    console.error('Failed to get auth token:', error);
    return null;
  }
}

/**
 * Get the current branch ID to use in API queries.
 * Returns the branch ID for filtering, or 'all' for multi-branch view.
 */
function getCurrentBranchForQuery(): string {
  return useBranchStore.getState().getCurrentBranchForQuery();
}

/**
 * The single branch currently selected, or undefined in the "All Branches"
 * view. Catalog (per-branch) calls use this instead of a hardcoded branch:
 * writes need a concrete branch (the server answers 400 branch_required to a
 * SuperAdmin who names none), and 'all' is never a valid write target.
 */
function selectedBranchId(): string | undefined {
  const id = getCurrentBranchForQuery();
  return id && id !== 'all' ? id : undefined;
}

/** `?branchId=<selected>` for catalog reads, or '' in the All Branches view. */
function catalogBranchQuery(): string {
  const id = selectedBranchId();
  return id ? `?branchId=${encodeURIComponent(id)}` : '';
}

/**
 * Add the current branch selection to URLSearchParams so the server can scope
 * the response. Sends the selected branch id, or the literal `all` for the
 * "All Branches" view — the server's branch middleware (requireBranchContext, which
 * the client routes all use) maps `all` to the caller's AUTHORIZED branches, never a
 * global scope. Omitting
 * the param entirely would make the server fall back to the caller's primary
 * branch, so `all` must be sent explicitly for the aggregate view. See issue #123.
 * @param params - URLSearchParams to modify
 * @param includeBranch - Whether to include branch param (default true)
 */
function addBranchParam(params: URLSearchParams, includeBranch = true): void {
  if (!includeBranch) return;
  const branchId = getCurrentBranchForQuery();
  if (branchId) {
    params.set('branchId', branchId);
  }
}

/** `/api/v1/clients/<id>?branchId=<selected or all>`: one client, scoped like the list (see addBranchParam). */
function clientUrl(id: string): string {
  const params = new URLSearchParams();
  addBranchParam(params);
  const query = params.toString();
  return `/api/v1/clients/${id}${query ? `?${query}` : ''}`;
}

// Default request timeout in milliseconds (30 seconds)
const DEFAULT_TIMEOUT_MS = 30000;

async function fetchApi<T>(
  endpoint: string,
  options?: RequestInit & { timeoutMs?: number },
  retryOnAuthError = true
): Promise<ApiResponse<T>> {
  const url = `${API_BASE}${endpoint}`;
  const timeoutMs = options?.timeoutMs ?? DEFAULT_TIMEOUT_MS;

  // Create AbortController for timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    // Get auth token for authenticated requests
    const token = await getAuthToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options?.headers as Record<string, string>),
    };

    // Add Authorization header if we have a token
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Add impersonation header if active (for simulator iframe)
    const impersonationToken = getImpersonationToken();
    if (impersonationToken) {
      headers['X-Impersonation-Token'] = impersonationToken;
    }

    const response = await fetch(url, {
      ...options,
      headers,
      signal: controller.signal,
    });

    // Handle auth errors with token refresh retry
    if ((response.status === 401 || response.status === 403) && retryOnAuthError) {
      console.log('Auth error, attempting token refresh...');
      const freshToken = await getAuthToken(true); // Force refresh
      if (freshToken) {
        headers['Authorization'] = `Bearer ${freshToken}`;
        // Retry with fresh token (but don't retry again if it fails)
        return fetchApi<T>(endpoint, { ...options, headers }, false);
      }
    }

    // Check for HTTP errors
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error || {
          code: `HTTP_${response.status}`,
          message: response.statusText || 'Request failed',
        },
      };
    }

    // Parse response body - handle JSON parse errors explicitly
    let data: ApiResponse<T>;
    try {
      data = await response.json();
    } catch (parseError) {
      // JSON parse error - response body is not valid JSON
      const errorDetails = parseError instanceof Error
        ? { name: parseError.name, message: parseError.message }
        : { type: typeof parseError, value: String(parseError) };

      console.error(`API JSON Parse Error [${endpoint}]:`, errorDetails);

      return {
        success: false,
        error: {
          code: 'PARSE_ERROR',
          message: `Failed to parse response: ${errorDetails.message || 'Invalid JSON'}`,
        },
      };
    }

    return data;
  } catch (error) {
    // Handle abort/timeout errors
    if (error instanceof Error && error.name === 'AbortError') {
      console.error(`API Timeout [${endpoint}]: ${timeoutMs}ms exceeded`);
      return {
        success: false,
        error: {
          code: 'TIMEOUT',
          message: `Request timed out after ${timeoutMs / 1000} seconds`,
        },
      };
    }

    // Extract meaningful error information for logging
    const errorDetails = error instanceof Error
      ? { name: error.name, message: error.message, stack: error.stack?.split('\n').slice(0, 3).join('\n') }
      : { type: typeof error, value: String(error), raw: error };

    console.error(`API Error [${endpoint}]:`, errorDetails);

    return {
      success: false,
      error: {
        code: 'NETWORK_ERROR',
        message: error instanceof Error ? error.message : 'Network error',
      },
    };
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Download a CSV endpoint as TEXT.
 *
 * fetchApi reads every successful body as JSON, so a CSV body comes back as PARSE_ERROR with no
 * data (payroll downloads used to save an empty file). This helper sends the same auth headers
 * as fetchApi and follows the same retry policy (one retry on 401/403 with a force-refreshed
 * token, 30s timeout, no retry on 5xx or timeout):
 * - 2xx with a CSV content type and a non-blank body: { success: true, data: <body verbatim> }
 * - 2xx with a blank body: EMPTY_CSV; 2xx that is not CSV: NOT_CSV. Neither may be saved.
 * - non-2xx: the server's own { code, message } when the body is JSON, else HTTP_<status>
 *   (so CSV_UNPARSEABLE reaches the person with its honest text).
 */
async function fetchCsvText(endpoint: string, authToken?: string | null, retryOnAuthError = true): Promise<ApiResponse<string>> {
  const url = `${API_BASE}${endpoint}`;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), DEFAULT_TIMEOUT_MS);

  try {
    const token = authToken === undefined ? await getAuthToken() : authToken;
    const headers: Record<string, string> = { Accept: 'text/csv' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const impersonationToken = getImpersonationToken();
    if (impersonationToken) headers['X-Impersonation-Token'] = impersonationToken;

    const response = await fetch(url, { headers, signal: controller.signal });

    if ((response.status === 401 || response.status === 403) && retryOnAuthError) {
      const freshToken = await getAuthToken(true);
      if (freshToken) return fetchCsvText(endpoint, freshToken, false);
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error || {
          code: `HTTP_${response.status}`,
          message: response.statusText || 'Request failed',
        },
      };
    }

    if (!(response.headers.get('content-type') ?? '').toLowerCase().includes('text/csv')) {
      return {
        success: false,
        error: { code: 'NOT_CSV', message: 'The server did not return a CSV file, so nothing was saved.' },
      };
    }
    const text = await response.text();
    if (text.trim() === '') {
      return {
        success: false,
        error: { code: 'EMPTY_CSV', message: 'The export came back empty, so nothing was saved. Please try again.' },
      };
    }
    return { success: true, data: text };
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      console.error(`API Timeout [${endpoint}]: ${DEFAULT_TIMEOUT_MS}ms exceeded`);
      return {
        success: false,
        error: { code: 'TIMEOUT', message: `Request timed out after ${DEFAULT_TIMEOUT_MS / 1000} seconds` },
      };
    }
    console.error(`API Error [${endpoint}]:`, error instanceof Error ? error.message : String(error));
    return {
      success: false,
      error: { code: 'NETWORK_ERROR', message: error instanceof Error ? error.message : 'Network error' },
    };
  } finally {
    clearTimeout(timeoutId);
  }
}

/** `/api/v1/finance/export/collections?tab=..&branchId=..&format=csv` (path only, no API_BASE). */
function collectionsExportPath(options?: { tab?: string; branchId?: string }): string {
  const params = new URLSearchParams();
  if (options?.tab) params.set('tab', options.tab);
  if (options?.branchId) params.set('branchId', options.branchId);
  params.set('format', 'csv');
  return `/api/v1/finance/export/collections?${params.toString()}`;
}

// Schedule types
export interface Schedule {
  id: number;
  date: string;
  employeeId: string | null;
  employeeName: string | null;
  employeeEmail: string | null;
  routeCode: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Route {
  id: number;
  routeCode: string;
  routeName: string | null;
  routeDay: string | null;
  primaryTechId: string | null;
  primaryTechName: string | null;
  notes: string | null;
}

// =============================================================================
// Service Region Types
// =============================================================================

export interface ServiceRegion {
  id: string;
  name: string;
  polygon: [number, number][]; // [lng, lat] pairs (GeoJSON standard)
  color: string;
}

// =============================================================================
// Email Sender Alias Types
// =============================================================================

export interface EmailSenderAlias {
  id: string;
  branchId: string;
  email: string;
  displayName: string;
  description: string | null;
  replyTo: string | null;
  isDefault: boolean;
  isVerified: boolean;
  verifiedAt: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
}

export interface AutomationEmailConfig {
  id: number;
  automationId: string;
  branchId: string;
  senderAliasId: string | null;
  updatedAt: string;
  updatedBy: string | null;
}

// =============================================================================
// Client Activity Types
// =============================================================================

export interface ClientActivity {
  id: number;
  action: string;
  timestamp: string;
  userId: string | null;
  userEmail: string | null;
  oldValues: Record<string, unknown> | null;
  newValues: Record<string, unknown> | null;
}

// =============================================================================
// Schedule Grid Types
// =============================================================================

export interface ScheduleGridTech {
  id: string;
  name: string;
  email: string;
  /** Primary route code for this tech (used for area grouping) */
  primaryRoute: string | null;
}

export interface ScheduleGridCell {
  scheduleId: number | null;
  routeCode: string | null;
  notes: string | null;
  isDraft: boolean;
  draftId: number | null;
  routeType: 'own' | 'coverage' | 'off' | 'shop' | 'empty';
}

/** Answer of POST /schedules/publish. `writeback` counts how many cells reached the Google Sheet. */
export interface PublishWeekResult {
  published: number;
  writeback?: { attempted: number; synced: number; skipped: number; failed: number; pending: number };
}

export interface ScheduleGridData {
  dates: string[];
  techs: ScheduleGridTech[];
  cells: Record<string, ScheduleGridCell>;
  routeOwnership: Record<string, string>;
}

export interface ScheduleValidation {
  valid: boolean;
  totalRoutes: number;
  scheduledRoutes: number;
  warnings: Array<{
    type: string;
    message: string;
    data?: unknown;
  }>;
}

export interface DraftSaveRequest {
  date: string;
  employeeId: string;
  employeeName?: string;
  routeCode?: string | null;
  notes?: string | null;
}

// Route type for dashboard alerts (matches database schema)
export interface RouteAlert {
  id: string;
  routeCode: string;
  employeeId: string;
  employeeName: string | null;
  totalDuration: number;
  stopCount: number;
  createdAt: string;
  updatedAt: string;
}

// Route data types for route management
export interface RouteData {
  id: string;
  routeCode: string;
  routeType: 'driving' | 'walking';
  employeeId: string;
  employeeName: string | null;
  totalDuration: number;
  stopCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface ClientStopData {
  id: string;
  clientName: string;
  businessName: string | null;
  stopOrder: number | null;
  stopDuration: number | null;
  address: {
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    zip?: string;
  } | null;
}

export interface RouteWithClientsData extends RouteData {
  clients: ClientStopData[];
  clientCount: number;
}

export interface RouteWithAreaCodes {
  id: string;
  routeCode: string;
  routeType: 'driving' | 'walking';
  employeeId: string;
  employeeName: string | null;
  totalDuration: number;
  stopCount: number;
  branchId: string | null;
  areaCodes: string[];
  primaryAreaCode: string | null;
}

export interface Client {
  id: string;
  clientName: string;
  businessName: string | null;
  isInactive: boolean;
  techId: string | null;
  routeId: string | null;
  stopOrder: number | null;
  stopDuration: number | null;
  address: {
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    zip?: string;
  } | null;
  servicePreferences: string | null;
  accessNotes: string | null;
  intervalDays: number | null;
  services: string[];
  monthlyAmount: string | null;
  areaCode: string | null;
  // Renewal fields
  renewalDate?: string | null;
  renewalNotes?: string | null;
  updatedAt?: string;
  // Contact fields
  contact?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  // Geocoding fields (for map display)
  latitude?: string | null;
  longitude?: string | null;
  geocodedAt?: string | null;
  // Inventory fields
  inventoryDocUrl?: string | null;
  inventoryStatus?: string | null;
  inventoryLastUpdated?: string | null;
  // Billing fields
  checkBillable?: string | null;
  // Cost tracking fields
  replacementSpend12Mo?: string | null;
  replacementSpend24Mo?: string | null;
  replacementRatio12Mo?: string | null;
  replacementRatio24Mo?: string | null;
  replacementEffectiveMonths12?: number | null;
  replacementEffectiveMonths24?: number | null;
  replacementLastCalculated?: string | null;
  // Virtual columns (optional, from includeServiceDates=true)
  lastServiceDate?: string | null;
  nextScheduledServiceDate?: string | null;
  // WO stats (populated by /schedules/employee/:email/today)
  openWorkorderCount?: number;
  hasUrgentWorkorder?: boolean;
  pickupCount?: number;
}

// Client cost metrics types
export interface ClientCostMetrics {
  spend12Mo: number;
  spend24Mo: number;
  ratio12Mo: number;
  ratio24Mo: number;
  effectiveMonths12: number;
  effectiveMonths24: number;
  lastCalculated: string | null;
}

export interface ClientQuarterlySummary {
  clientId: string;
  quarter: string;
  replacementWholesale: number;
  replacementRetail: number;
  workorderCount: number;
  monthlyBillingAmount: number;
  quarterlyBillingTotal: number;
}

export interface ClientCostHistory {
  client: {
    id: string;
    clientName: string;
    monthlyBilling: number;
  };
  currentMetrics: ClientCostMetrics;
  quarterlyHistory: ClientQuarterlySummary[];
}

export interface ClientCostCalculationResult {
  metrics: {
    clientId: string;
    spend12Mo: number;
    spend24Mo: number;
    ratio12Mo: number;
    ratio24Mo: number;
    monthlyBilling: number;
    effectiveMonths12: number;
    effectiveMonths24: number;
    workorderCount12Mo: number;
    workorderCount24Mo: number;
  };
  quarterly: ClientQuarterlySummary;
}

// Contact types for client contacts
export type ContactType = 'service' | 'sales' | 'billing' | 'property_manager' | 'executive' | 'other';

export interface ClientContact {
  id: number;
  clientId: string;
  name: string;
  email: string | null;
  phone: string | null;
  contactTypes: ContactType[];
  customType: string | null;
  notes: string | null;
  isPrimary: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateContactInput {
  name: string;
  email?: string;
  phone?: string;
  contactTypes?: ContactType[];
  customType?: string;
  notes?: string;
  isPrimary?: boolean;
}

export interface UpdateContactInput {
  name?: string;
  email?: string | null;
  phone?: string | null;
  contactTypes?: ContactType[];
  customType?: string | null;
  notes?: string | null;
  isPrimary?: boolean;
}

// Aging client (extends Client with service history)
export interface AgingClient {
  id: string;
  clientName: string;
  businessName: string | null;
  techId: string | null;
  routeId: string | null;
  intervalDays: number;
  areaCode: string | null;
  address: Record<string, unknown> | null;
  accessNotes: string | null;
  lastServiceDate: string | null;
  lastServiceTech: string | null;
  daysSinceService: number;
  multiplier: number;
  severity: 'warning' | 'danger' | 'critical' | 'ok';
  // Dismissal info (F4 Phase 2)
  isDismissed?: boolean;
  dismissalId?: number;
  dismissedAt?: string;
  dismissedByName?: string;
  reasonCode?: string;
  reasonNote?: string;
  dismissalType?: 'one_time' | 'ongoing';
}

// Client with missing data fields
export interface MissingDataClient {
  id: string;
  clientName: string;
  businessName: string | null;
  techId: string | null;
  routeId: string | null;
  areaCode: string | null;
  intervalDays: number | null;
  stopOrder: number | null;
  stopDuration: number | null;
  monthlyAmount: string | null;
  services: unknown;
  address: Record<string, unknown> | null;
  missingFields: string[];
  // Dismissal info (F4 Phase 2)
  isDismissed?: boolean;
  dismissalId?: number;
  dismissedAt?: string;
  dismissedByName?: string;
  reasonCode?: string;
  dismissalType?: 'one_time' | 'ongoing';
}

export interface SuspiciousDataClient {
  id: string;
  clientName: string;
  businessName: string | null;
  routeId: string | null;
  issueType: 'missing_geocode' | 'geocode_outlier' | 'invalid_interval' | 'duplicate_name' | 'tech_mismatch';
  issueDetail: string | null;
  // Dismissal info (F4 Phase 2)
  isDismissed?: boolean;
  dismissalId?: number;
  dismissedAt?: string;
  dismissedByName?: string;
  reasonCode?: string;
  dismissalType?: 'one_time' | 'ongoing';
}

export interface TechMismatchClient {
  id: string;
  clientName: string;
  businessName: string | null;
  routeId: string;
  assignedTechId: string;
  assignedTechName: string | null;
  primaryTechId: string;
  primaryTechName: string | null;
  routeClientCount: number;
  primaryTechClientCount: number;
  issueDetail: string;
}

// Suspicious Location Alert types
export interface SuspiciousLocationLog {
  id: number;
  clientId: string;
  clientName: string | null;
  timestamp: string;
  distanceFromClient: number;
}

export interface SuspiciousLocationAlert {
  employeeId: string;
  employeeName: string | null;
  alertDate: string;
  serviceLogCount: number;
  logs: SuspiciousLocationLog[];
  isDismissed: boolean;
  dismissedAt: string | null;
  dismissedBy: string | null;
  dismissalComment: string | null;
}

export interface DismissLocationAlertInput {
  employeeId: string;
  alertDate: string;
  comment: string;
}

export interface DismissLocationAlertResponse {
  id: number;
  serviceLogCount: number;
  dismissedAt: string;
}

export interface OfficeNote {
  id: number;
  timestamp: string;
  clientId: string;
  clientName: string | null;
  userEmail: string | null;
  userName: string | null;
  note: string | null;
}

// Pickup item for tech's route
export interface PickupItem {
  id: number;
  clientId: string;
  clientName: string | null;
  description: string;
}

export interface TodayScheduleResponse {
  schedule: Schedule;
  route: Route | null;
  clients: Client[];
  pickupItems?: PickupItem[];
}

// Work Order types
export interface WorkOrder {
  id: number;
  firestoreId: string | null;
  status: string;
  urgent: boolean;
  submittedBy: string | null;
  submittedByName: string | null;
  assignedTo: string | null;
  assignedToName: string | null;
  woRun: string | null;
  qty: number;
  itemName: string | null;
  clientId: string;
  clientName: string | null;
  clientAreaCode?: string | null;  // Included in ready-for-pickup endpoint
  branchId: string | null;
  location: string | null;
  currentLocationId: string | null;
  currentLocationName: string | null;
  notes: string | null;
  billable: boolean;
  billingNotes: string | null;
  deadline: string | null;
  floral: boolean;
  statusComplete: string | null;
  timestamp: string;
  inventoryPlantId: string | null;
  catalogItemId: number | null;
  replacementQuantity: number | null;
  createdAt: string;
  // Image fields (Firebase Storage URLs after migration)
  imageUrl: string | null;
  imageUrl2: string | null;
  imageUrl3: string | null;
  imageUrl4: string | null;
  opsImageUrl1: string | null;
  opsImageUrl2: string | null;
  // Order tracking fields (synced from Sheets)
  supplier?: string | null;
  orderNumber?: string | null;
  orderDate?: string | null;  // YYYY-MM-DD format
  // Cost tracking fields (calculated on completion)
  replacementWholesaleCost?: string | null;
  replacementRetailCost?: string | null;
  costLookupMethod?: string | null;
  costLookupMatch?: string | null;
  // Virtual columns
  detailedDescription?: string;
  ageDays?: number;
  stage?: number;
  itemSize?: string | null;
  assignedToRun?: string | null;
  wholesaleCostEstimate?: number | null;
  // Billing tracking
  billedAt?: string | null;
  billedBy?: string | null;
  // Stalled workorder info (from stalled alerts endpoint)
  lastStatusChange?: string | null;
  stalledDays?: number;
  stalledSince?: string;
  thresholdDays?: number;
}

export interface WorkOrdersResponse {
  data: WorkOrder[];
  meta: {
    total: number;
    limit: number;
    offset: number;
  };
}

export interface WorkOrderFilters {
  search?: string;
  status?: string;
  clientId?: string;
  assignedTo?: string;
  open?: boolean;
  limit?: number;
  offset?: number;
  sort?: string;
  order?: 'asc' | 'desc';
}

export interface WorkOrderStats {
  total: number;
  open: number;
  complete: number;
  declined: number;
  urgent: number;
  byStatus: { status: string; count: number }[];
}

// Run Planner types
export interface WorkOrderWithCoords extends WorkOrder {
  clientLatitude: string | null;
  clientLongitude: string | null;
  clientAccessNotes: string | null;
  runSequence: number | null;
  opsNotes: string | null;
  address: string | null;
}

export interface RunSummary {
  runName: string;
  assignedToName: string | null;
  count: number;
}

export interface Employee {
  id: string;
  name: string;
  email: string;
  cell: string | null;
  title: string | null;
  role: string;
  device: string | null;
  supervisorId: string | null;
  birthday: string | null;
  vehicle: string | null;
  startDate: string | null;
  inactiveDate: string | null;
  homeAddress: string | null;
  homeLatitude: string | null;  // Geocoded from homeAddress
  homeLongitude: string | null; // Geocoded from homeAddress
  isActive: boolean;
  isSuperAdmin?: boolean;
  viewTags?: string[];
  todaysRoute?: string | null;
  profilePhotoUrl?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

// =============================================================================
// Automations Types
// =============================================================================

export interface Automation {
  id: string;
  name: string;
  description: string | null;
  category: string;
  triggerType: string;
  triggerConfig: Record<string, unknown> | null;
  enabled: boolean;
  emailSubject: string | null;
  emailTemplate: string | null;
  config: Record<string, unknown> | null;
  lastConfigChange: string | null;
  createdAt: string;
  updatedAt: string;
  lastRun: AutomationRun | null;
}

export interface AutomationStep {
  id: string;
  label: string;
  status: 'running' | 'success' | 'failed' | 'skipped';
  startedAt: string;
  completedAt?: string;
  durationMs?: number;
  details?: Record<string, unknown>;
  error?: string;
}

export interface AffectedEntity {
  entityType: string;
  entityId: string;
  entityName?: string;
}

export interface AutomationRun {
  id: number;
  automationId: string;
  startedAt: string;
  completedAt: string | null;
  status: 'running' | 'success' | 'failed';
  trigger: string;
  result: Record<string, unknown> | null;
  error: string | null;
  triggeredBy: string | null;
  // New fields for step tracing
  steps?: AutomationStep[];
  context?: Record<string, unknown>;
  durationMs?: number;
  affectedEntities?: AffectedEntity[];
  // Joined data
  automationName?: string;
}

export interface AutomationsResponse {
  automations: Automation[];
  grouped: Record<string, Automation[]>;
  summary: {
    total: number;
    enabled: number;
    categories: number;
  };
}

export interface AutomationRecipient {
  id: number;
  automationId: string;
  recipientType: 'email' | 'role' | 'user' | 'branch' | 'variable';
  recipientValue: string;
  displayName: string | null;
  isCc: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Variable recipient options for automation notifications */
export const VARIABLE_RECIPIENT_OPTIONS = [
  { value: 'requestor', label: 'Requestor', description: 'Person who submitted the request' },
  { value: 'assigned_tech', label: 'Assigned Tech', description: 'Technician assigned to the item' },
  { value: 'client_contact', label: 'Client Contact', description: 'Primary contact for the client' },
  { value: 'client_email', label: 'Client Email', description: 'Client email address' },
  { value: 'branch_manager', label: 'Branch Manager', description: 'Manager of the branch' },
] as const;

// API functions
/** GET /api/v1/clients/map-locations: stored pins only, plus what staff need to know about clients without one. */
export interface ClientMapLocationsData {
  /** Clients drawn on the map. */
  total: number;
  /** No pin: waitingCount + unfindableCount. */
  missingLocation: number;
  /** No pin yet; placed automatically, nothing to do. */
  waitingCount: number;
  /** No pin and none coming without a person: the address cannot be found, or has no street. */
  unfindableCount: number;
  /** The first of those clients (the count is exact, the list is capped), to open and fix. */
  unfindableClients: Array<{ id: string; clientName: string }>;
  clients: Array<{
    id: string;
    clientName: string;
    businessName: string | null;
    routeId: string | null;
    techId: string | null;
    stopOrder: number | null;
    address: {
      address1?: string;
      address2?: string;
      city?: string;
      state?: string;
      zip?: string;
    } | null;
    latitude: string | null;
    longitude: string | null;
    intervalDays: number | null;
    stopDuration: number | null;
    isInactive: boolean;
  }>;
}

export const api = {
  // Work Orders
  workorders: {
    list: (filters?: WorkOrderFilters & { branchId?: string }) => {
      const params = new URLSearchParams();
      if (filters?.search) params.set('search', filters.search);
      if (filters?.status) params.set('status', filters.status);
      if (filters?.clientId) params.set('clientId', filters.clientId);
      if (filters?.assignedTo) params.set('assignedTo', filters.assignedTo);
      if (filters?.open !== undefined) params.set('open', String(filters.open));
      if (filters?.limit) params.set('limit', String(filters.limit));
      if (filters?.offset) params.set('offset', String(filters.offset));
      if (filters?.sort) params.set('sort', filters.sort);
      if (filters?.order) params.set('order', filters.order);
      // Add branch filter (uses store's current branch if not explicitly provided)
      if (filters?.branchId) {
        params.set('branchId', filters.branchId);
      } else {
        addBranchParam(params);
      }
      const query = params.toString() ? `?${params.toString()}` : '';
      return fetchApi<WorkOrder[]>(`/api/v1/workorders${query}`);
    },

    getOpen: () => fetchApi<WorkOrder[]>('/api/v1/workorders/open'),

    getById: (id: number) => fetchApi<WorkOrder>(`/api/v1/workorders/${id}`),
    get: (id: string | number) =>
      fetchApi<WorkOrder>(`/api/v1/workorders/${encodeURIComponent(String(id))}`),

    getByClient: (clientId: string, open?: boolean) => {
      const params = open !== undefined ? `?open=${open}` : '';
      return fetchApi<WorkOrder[]>(`/api/v1/workorders/by-client/${clientId}${params}`);
    },

    getStats: () => fetchApi<WorkOrderStats>('/api/v1/workorders/stats/summary'),

    create: (data: Partial<WorkOrder>) =>
      fetchApi<WorkOrder>('/api/v1/workorders', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (id: number, data: Partial<WorkOrder>) =>
      fetchApi<WorkOrder>(`/api/v1/workorders/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    updateStatus: (id: number, status: string) =>
      fetchApi<WorkOrder>(`/api/v1/workorders/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      }),

    /** Mark a billable workorder as billed. Who billed it is recorded by the server from the signed-in user. */
    markBilled: (id: number, invoiceNumber?: string) =>
      fetchApi<WorkOrder>(`/api/v1/workorders/${id}/mark-billed`, {
        method: 'PATCH',
        body: JSON.stringify({ invoiceNumber }),
      }),

    /** Remove billed status from a workorder */
    unmarkBilled: (id: number) =>
      fetchApi<WorkOrder>(`/api/v1/workorders/${id}/unmark-billed`, {
        method: 'PATCH',
      }),

    // Run Planner endpoints
    /** Get work orders ready for delivery with client coordinates */
    getReadyForRun: () => {
      const params = new URLSearchParams();
      addBranchParam(params);
      const query = params.toString() ? `?${params.toString()}` : '';
      return fetchApi<WorkOrderWithCoords[]>(`/api/v1/workorders/ready-for-run${query}`);
    },

    /** Get unique run names with counts */
    getRuns: () => {
      const params = new URLSearchParams();
      addBranchParam(params);
      const query = params.toString() ? `?${params.toString()}` : '';
      return fetchApi<RunSummary[]>(`/api/v1/workorders/runs${query}`);
    },

    /** Get work orders for a specific run */
    getRunWorkorders: (runName: string) => {
      const params = new URLSearchParams();
      addBranchParam(params);
      const query = params.toString() ? `?${params.toString()}` : '';
      return fetchApi<WorkOrderWithCoords[]>(`/api/v1/workorders/runs/${encodeURIComponent(runName)}${query}`);
    },

    /** Assign multiple work orders to a run */
    batchAssignRun: (data: { runName: string; assignedTo?: string; assignedToName?: string; workorders: Array<{ id: number; sequence: number }> }) =>
      fetchApi<{ runName: string; updatedCount: number; workorders: WorkOrder[] }>('/api/v1/workorders/batch-assign-run', {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    /** Batch receive workorders at a location */
    batchReceive: (data: { workorderIds: number[]; locationId: string | null; locationName: string }) =>
      fetchApi<{ updatedCount: number; workorders: WorkOrder[] }>('/api/v1/workorders/batch-receive', {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    /** Batch update workorder status */
    batchStatus: (data: { workorderIds: number[]; status: string }) =>
      fetchApi<{ updatedCount: number; status: string; workorders: WorkOrder[] }>('/api/v1/workorders/batch-status', {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    /** Complete a delivery with optional photos */
    completeDelivery: (id: number, data: { opsImageUrl1?: string; opsImageUrl2?: string; opsNotes?: string }) =>
      fetchApi<WorkOrder>(`/api/v1/workorders/${id}/complete-delivery`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    /** Remove a work order from its run */
    removeFromRun: (id: number) =>
      fetchApi<WorkOrder>(`/api/v1/workorders/${id}/remove-from-run`, {
        method: 'PATCH',
      }),

    /** Soft-delete (archive) a work order */
    delete: (id: number) =>
      fetchApi<{ id: number; archived: boolean; archivedAt: string }>(
        `/api/v1/workorders/${id}`,
        { method: 'DELETE' }
      ),
  },

  // Employees
  employees: {
    list: (activeOnly = false) => {
      const params = activeOnly ? '?active=true' : '';
      return fetchApi<Employee[]>(`/api/v1/employees${params}`);
    },

    getById: (id: string) => fetchApi<Employee>(`/api/v1/employees/${id}`),

    create: (data: Partial<Employee>) =>
      fetchApi<Employee>('/api/v1/employees', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (id: string, data: Partial<Employee>) =>
      fetchApi<Employee>(`/api/v1/employees/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
  },

  // Clients
  clients: {
    list: (options?: { activeOnly?: boolean; includeServiceDates?: boolean; limit?: number; branchId?: string } | boolean) => {
      // Support legacy boolean signature
      const opts = typeof options === 'boolean'
        ? { activeOnly: options, includeServiceDates: false, limit: 10000 }
        : { activeOnly: false, includeServiceDates: false, limit: 10000, ...options };

      const params = new URLSearchParams();
      if (opts.activeOnly) params.set('active', 'true');
      if (opts.includeServiceDates) params.set('includeServiceDates', 'true');
      params.set('limit', String(opts.limit)); // Always request all records
      // Add branch filter (uses store's current branch if not explicitly provided)
      if (opts.branchId) {
        params.set('branchId', opts.branchId);
      } else {
        addBranchParam(params);
      }
      const queryString = params.toString();
      return fetchApi<Client[]>(`/api/v1/clients${queryString ? `?${queryString}` : ''}`);
    },

    /**
     * One client, read with the branch selection the list was read with (T4): without it the server scopes the read to the
     * person's DEFAULT branch, and a multi-branch person who opened a client of another of their branches from the All
     * Branches list was refused (403) for a client the list had just shown them.
     */
    getById: (id: string) => fetchApi<Client>(clientUrl(id)),
    get: (id: string) => fetchApi<Client>(clientUrl(id)),

    /**
     * Clients for the Client Map, from STORED pins only. Read-only: it never geocodes or writes.
     * (The old POST /clients/geocode looked up and saved every missing pin each time the map opened.)
     * Scoped to the selected branch ('all' for the All Branches view): without the branch the server falls
     * back to the caller's primary branch, which would hide every other branch's pins (see addBranchParam).
     */
    mapLocations: () => {
      const params = new URLSearchParams();
      addBranchParam(params);
      const queryString = params.toString();
      return fetchApi<ClientMapLocationsData>(`/api/v1/clients/map-locations${queryString ? `?${queryString}` : ''}`);
    },

    /**
     * `branchId`: the branch to create the client in. Defaults to the branch selected on screen, like every other write. In
     * the "All Branches" view none is sent, and the server answers 400 branch_required to a SuperAdmin (a write never guesses
     * its branch); a branch the caller is not entitled to is a 403, never quietly swapped for another.
     */
    create: (data: Partial<Client> & { branchId?: string }) =>
      fetchApi<Client>('/api/v1/clients', {
        method: 'POST',
        body: JSON.stringify({ ...data, branchId: data.branchId ?? selectedBranchId() }),
      }),

    /** `pinPlacedByHand`: a person placed the pin in `latitude`/`longitude` (not a column; the server uses it to lock the pin). */
    update: (id: string, data: Partial<Client> & { pinPlacedByHand?: boolean }) =>
      fetchApi<Client>(`/api/v1/clients/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    /** Re-geocode a single client's address */
    geocodeOne: (id: string) =>
      fetchApi<GeocodeResult>(`/api/v1/clients/${id}/geocode`, {
        method: 'POST',
      }),

    /** Get activity history for a client */
    getActivity: (id: string, options?: { action?: string; limit?: number; offset?: number }) => {
      const params = new URLSearchParams();
      if (options?.action) params.set('action', options.action);
      if (options?.limit) params.set('limit', String(options.limit));
      if (options?.offset) params.set('offset', String(options.offset));
      const query = params.toString();
      return fetchApi<ClientActivity[]>(`/api/v1/clients/${id}/activity${query ? `?${query}` : ''}`);
    },

    /**
     * Suggest area codes based on ZIP code
     * Returns suggested codes (matching ZIPs) and all available codes
     */
    suggestAreaCodes: (zip: string) =>
      fetchApi<{
        suggested: string[];
        matchType: 'exact' | 'prefix4' | 'prefix3' | 'none';
        other: string[];
        all: string[];
      }>(`/api/v1/clients/area-codes/suggest?zip=${encodeURIComponent(zip)}`),

    // Client Cost Metrics
    calculateCosts: (clientId: string) =>
      fetchApi<ClientCostCalculationResult>(`/api/v1/clients/${clientId}/calculate-costs`, {
        method: 'POST',
      }),

    getCostHistory: (clientId: string, limit = 8) =>
      fetchApi<ClientCostHistory>(`/api/v1/clients/${clientId}/cost-history?limit=${limit}`),

    calculateAllCosts: (daysBack = 30) =>
      fetchApi<{ processed: number; errors: number }>('/api/v1/clients/calculate-all-costs', {
        method: 'POST',
        body: JSON.stringify({ daysBack }),
      }),

    // Client Contacts
    contacts: {
      list: (clientId: string) =>
        fetchApi<ClientContact[]>(`/api/v1/clients/${clientId}/contacts`),

      create: (clientId: string, data: CreateContactInput) =>
        fetchApi<ClientContact>(`/api/v1/clients/${clientId}/contacts`, {
          method: 'POST',
          body: JSON.stringify(data),
        }),

      update: (clientId: string, contactId: number, data: UpdateContactInput) =>
        fetchApi<ClientContact>(`/api/v1/clients/${clientId}/contacts/${contactId}`, {
          method: 'PATCH',
          body: JSON.stringify(data),
        }),

      delete: (clientId: string, contactId: number) =>
        fetchApi<{ id: number; deleted: boolean }>(`/api/v1/clients/${clientId}/contacts/${contactId}`, {
          method: 'DELETE',
        }),

      migrate: () =>
        fetchApi<{ migrated: number; skipped: number; message: string }>('/api/v1/clients/migrate-contacts', {
          method: 'POST',
        }),
    },
  },

  // Office Notes
  officeNotes: {
    list: (options?: { clientId?: string; limit?: number }) => {
      const params = new URLSearchParams();
      if (options?.clientId) params.set('clientId', options.clientId);
      if (options?.limit) params.set('limit', String(options.limit));
      const queryString = params.toString();
      return fetchApi<OfficeNote[]>(`/api/v1/office-notes${queryString ? `?${queryString}` : ''}`);
    },

    getByClient: (clientId: string, limit = 20) =>
      fetchApi<OfficeNote[]>(`/api/v1/office-notes/by-client/${encodeURIComponent(clientId)}?limit=${limit}`),

    create: (data: { clientId: string; clientName?: string; note: string }) =>
      fetchApi<OfficeNote>('/api/v1/office-notes', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (id: number, note: string) =>
      fetchApi<OfficeNote>(`/api/v1/office-notes/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ note }),
      }),

    delete: (id: number) =>
      fetchApi<{ id: number; deleted: boolean }>(`/api/v1/office-notes/${id}`, {
        method: 'DELETE',
      }),
  },

  // Schedules
  schedules: {
    getToday: () =>
      fetchApi<Schedule[]>('/api/v1/schedules/today'),

    getEmployeeToday: (email: string, date?: string) => {
      const params = date ? `?date=${date}` : '';
      return fetchApi<TodayScheduleResponse>(`/api/v1/schedules/employee/${encodeURIComponent(email)}/today${params}`);
    },

    getEmployeeSchedules: (email: string, startDate?: string, endDate?: string) => {
      const params = new URLSearchParams();
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
      const query = params.toString() ? `?${params.toString()}` : '';
      return fetchApi<Schedule[]>(`/api/v1/schedules/employee/${encodeURIComponent(email)}${query}`);
    },

    getWeek: (startDate?: string) => {
      const params = startDate ? `?startDate=${startDate}` : '';
      return fetchApi<Record<string, Schedule[]>>(`/api/v1/schedules/week${params}`);
    },

    getGrid: async (startDate?: string, weeks?: number): Promise<ApiResponse<ScheduleGridData>> => {
      const params = new URLSearchParams();
      if (startDate) params.set('startDate', startDate);
      if (weeks) params.set('weeks', weeks.toString());
      const query = params.toString() ? `?${params.toString()}` : '';
      return fetchApi<ScheduleGridData>(`/api/v1/schedules/grid${query}`);
    },

    saveDraft: async (data: DraftSaveRequest): Promise<ApiResponse<{ draftId: number }>> => {
      return fetchApi<{ draftId: number }>('/api/v1/schedules/draft', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    publishWeek: async (weekStart: string): Promise<ApiResponse<PublishWeekResult>> => {
      return fetchApi<PublishWeekResult>('/api/v1/schedules/publish', {
        method: 'POST',
        body: JSON.stringify({ weekStart }),
      });
    },

    discardWeek: async (weekStart: string): Promise<ApiResponse<{ discarded: number }>> => {
      return fetchApi<{ discarded: number }>('/api/v1/schedules/discard', {
        method: 'POST',
        body: JSON.stringify({ weekStart }),
      });
    },

    validate: async (startDate?: string, endDate?: string): Promise<ApiResponse<ScheduleValidation>> => {
      const params = new URLSearchParams();
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
      const query = params.toString() ? `?${params.toString()}` : '';
      return fetchApi<ScheduleValidation>(`/api/v1/schedules/validate${query}`);
    },

    /** Get schedule availability for calendar display - returns dates that have schedules */
    getAvailability: async (startDate?: string, endDate?: string): Promise<ApiResponse<Record<string, boolean>>> => {
      const params = new URLSearchParams();
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
      const query = params.toString() ? `?${params.toString()}` : '';
      return fetchApi<Record<string, boolean>>(`/api/v1/schedules/availability${query}`);
    },
  },

  // Health check
  health: () => fetchApi<{ status: string; database: string }>('/api/v1/health'),

  // Service Logs
  serviceLogs: {
    list: (filters?: {
      employeeId?: string;
      clientId?: string;
      startDate?: string;
      endDate?: string;
      limit?: number;
      offset?: number;
      branchId?: string;
    }) => {
      const params = new URLSearchParams();
      if (filters?.employeeId) params.set('employeeId', filters.employeeId);
      if (filters?.clientId) params.set('clientId', filters.clientId);
      if (filters?.startDate) params.set('startDate', filters.startDate);
      if (filters?.endDate) params.set('endDate', filters.endDate);
      if (filters?.limit) params.set('limit', String(filters.limit));
      if (filters?.offset) params.set('offset', String(filters.offset));
      // Add branch filter (uses store's current branch if not explicitly provided)
      if (filters?.branchId) {
        params.set('branchId', filters.branchId);
      } else {
        addBranchParam(params);
      }
      const query = params.toString() ? `?${params.toString()}` : '';
      return fetchApi<ServiceLog[]>(`/api/v1/service-logs${query}`);
    },

    getById: (id: number) => fetchApi<ServiceLog>(`/api/v1/service-logs/${id}`),

    create: (data: {
      clientId: string;
      employeeId?: string;
      timestamp?: string;
      missedService?: string | null;
      serviceNotes?: string | null;
      latitude?: string | null;
      longitude?: string | null;
      imageUrl?: string | null;
    }) =>
      fetchApi<ServiceLog>('/api/v1/service-logs', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (id: number, data: {
      serviceNotes?: string | null;
      missedService?: string | null;
      imageUrl?: string | null;
      latitude?: string | null;
      longitude?: string | null;
      clientId?: string;
      clientName?: string;
      employeeId?: string;
      employeeName?: string;
      timestamp?: string;
    }) =>
      fetchApi<ServiceLog>(`/api/v1/service-logs/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    delete: (id: number) =>
      fetchApi<{ id: number; deleted: boolean }>(`/api/v1/service-logs/${id}`, {
        method: 'DELETE',
      }),
  },

  // Dashboard
  dashboard: {
    getAlertsSummary: (branchId?: string) => {
      const params = new URLSearchParams();
      if (branchId) {
        params.set('branchId', branchId);
      } else {
        addBranchParam(params);
      }
      const query = params.toString() ? `?${params.toString()}` : '';
      return fetchApi<DashboardAlertSummary>(`/api/v1/dashboard/alerts/summary${query}`);
    },

    getUrgentWorkorders: (limit?: number, offset?: number) => {
      const params = new URLSearchParams();
      if (limit) params.set('limit', String(limit));
      if (offset) params.set('offset', String(offset));
      addBranchParam(params);
      const query = params.toString() ? `?${params.toString()}` : '';
      return fetchApi<WorkOrder[]>(`/api/v1/dashboard/alerts/workorder/urgent${query}`);
    },

    getStalledWorkorders: (limit?: number, offset?: number) => {
      const params = new URLSearchParams();
      if (limit) params.set('limit', String(limit));
      if (offset) params.set('offset', String(offset));
      addBranchParam(params);
      const query = params.toString() ? `?${params.toString()}` : '';
      return fetchApi<WorkOrder[]>(`/api/v1/dashboard/alerts/workorder/stalled${query}`);
    },

    getReadyForPickup: (limit?: number, offset?: number) => {
      const params = new URLSearchParams();
      if (limit) params.set('limit', String(limit));
      if (offset) params.set('offset', String(offset));
      addBranchParam(params);
      const query = params.toString() ? `?${params.toString()}` : '';
      return fetchApi<WorkOrder[]>(`/api/v1/dashboard/alerts/workorder/ready-for-pickup${query}`);
    },

    getClientsNoRoute: (limit?: number, offset?: number) => {
      const params = new URLSearchParams();
      if (limit) params.set('limit', String(limit));
      if (offset) params.set('offset', String(offset));
      addBranchParam(params);
      const query = params.toString() ? `?${params.toString()}` : '';
      return fetchApi<Client[]>(`/api/v1/dashboard/alerts/admin/no-route${query}`);
    },

    getMissedServices: (date?: string, limit?: number, offset?: number) => {
      const params = new URLSearchParams();
      if (date) params.set('date', date);
      if (limit) params.set('limit', String(limit));
      if (offset) params.set('offset', String(offset));
      addBranchParam(params);
      const query = params.toString() ? `?${params.toString()}` : '';
      return fetchApi<ServiceLog[]>(`/api/v1/dashboard/alerts/client/missed-service${query}`);
    },

    getPartialMissedServices: (limit?: number, offset?: number) => {
      const params = new URLSearchParams();
      if (limit) params.set('limit', String(limit));
      if (offset) params.set('offset', String(offset));
      addBranchParam(params);
      const query = params.toString() ? `?${params.toString()}` : '';
      return fetchApi<ServiceLog[]>(`/api/v1/dashboard/alerts/client/partial-missed${query}`);
    },

    getMissedServiceActions: (resolved?: boolean, limit?: number, offset?: number) => {
      const params = new URLSearchParams();
      if (resolved !== undefined) params.set('resolved', String(resolved));
      if (limit) params.set('limit', String(limit));
      if (offset) params.set('offset', String(offset));
      addBranchParam(params);
      const query = params.toString() ? `?${params.toString()}` : '';
      return fetchApi<MissedServiceAction[]>(`/api/v1/dashboard/missed-service-actions${query}`);
    },

    createMissedServiceAction: (data: CreateMissedServiceActionInput) => {
      return fetchApi<{ id: number }>('/api/v1/dashboard/missed-service-actions', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    updateMissedServiceAction: (id: number, data: UpdateMissedServiceActionInput) => {
      return fetchApi<{ id: number }>(`/api/v1/dashboard/missed-service-actions/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    },

    getRoutesNotScheduled: (days?: number, limit?: number, offset?: number) => {
      const params = new URLSearchParams();
      if (days) params.set('days', String(days));
      if (limit) params.set('limit', String(limit));
      if (offset) params.set('offset', String(offset));
      addBranchParam(params);
      const query = params.toString() ? `?${params.toString()}` : '';
      return fetchApi<RouteAlert[]>(`/api/v1/dashboard/alerts/admin/route-not-scheduled${query}`);
    },

    getClientsNoNextVisit: (days?: number, limit?: number, offset?: number) => {
      const params = new URLSearchParams();
      if (days) params.set('days', String(days));
      if (limit) params.set('limit', String(limit));
      if (offset) params.set('offset', String(offset));
      addBranchParam(params);
      const query = params.toString() ? `?${params.toString()}` : '';
      return fetchApi<Client[]>(`/api/v1/dashboard/alerts/client/no-next-visit${query}`);
    },

    getAgingClients: (threshold?: number, limit?: number, offset?: number) => {
      const params = new URLSearchParams();
      if (threshold) params.set('threshold', String(threshold));
      if (limit) params.set('limit', String(limit));
      if (offset) params.set('offset', String(offset));
      addBranchParam(params);
      const query = params.toString() ? `?${params.toString()}` : '';
      return fetchApi<AgingClient[]>(`/api/v1/dashboard/alerts/client/aging${query}`);
    },

    getClientsWithMissingData: (limit?: number, offset?: number) => {
      const params = new URLSearchParams();
      if (limit) params.set('limit', String(limit));
      if (offset) params.set('offset', String(offset));
      addBranchParam(params);
      const query = params.toString() ? `?${params.toString()}` : '';
      return fetchApi<MissingDataClient[]>(`/api/v1/dashboard/alerts/admin/missing-data${query}`);
    },

    getSuspiciousData: (limit?: number, offset?: number) => {
      const params = new URLSearchParams();
      if (limit) params.set('limit', String(limit));
      if (offset) params.set('offset', String(offset));
      addBranchParam(params);
      const query = params.toString() ? `?${params.toString()}` : '';
      return fetchApi<SuspiciousDataClient[]>(`/api/v1/dashboard/alerts/admin/suspicious-data${query}`);
    },

    getTechMismatches: (limit?: number, offset?: number) => {
      const params = new URLSearchParams();
      if (limit) params.set('limit', String(limit));
      if (offset) params.set('offset', String(offset));
      addBranchParam(params);
      const query = params.toString() ? `?${params.toString()}` : '';
      return fetchApi<TechMismatchClient[]>(`/api/v1/dashboard/alerts/admin/tech-mismatch${query}`);
    },

    getSuspiciousLocations: (includeDismissed?: boolean) => {
      const params = new URLSearchParams();
      if (includeDismissed) params.set('includeDismissed', 'true');
      addBranchParam(params);
      const query = params.toString() ? `?${params.toString()}` : '';
      return fetchApi<SuspiciousLocationAlert[]>(`/api/v1/dashboard/alerts/admin/suspicious-locations${query}`);
    },

    dismissLocationAlert: (data: DismissLocationAlertInput) => {
      return fetchApi<DismissLocationAlertResponse>('/api/v1/dashboard/alerts/location/dismiss', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    // Master Schedule
    getMasterScheduleSummary: (date?: string) => {
      const params = new URLSearchParams();
      if (date) params.set('date', date);
      addBranchParam(params);
      const query = params.toString() ? `?${params.toString()}` : '';
      return fetchApi<MasterScheduleSummary>(`/api/v1/dashboard/master-schedule/summary${query}`);
    },

    getMasterScheduleTechs: (date?: string) => {
      const params = new URLSearchParams();
      if (date) params.set('date', date);
      addBranchParam(params);
      const query = params.toString() ? `?${params.toString()}` : '';
      return fetchApi<TechScheduleRow[]>(`/api/v1/dashboard/master-schedule/techs${query}`);
    },
  },

  // Routes
  routes: {
    list: (employeeId?: string) => {
      const params = employeeId ? `?employeeId=${employeeId}` : '';
      return fetchApi<RouteData[]>(`/api/v1/routes${params}`);
    },

    /** Get all routes with their area codes (derived from clients) for route selector */
    getWithAreaCodes: () =>
      fetchApi<RouteWithAreaCodes[]>('/api/v1/routes/with-area-codes'),

    getById: (id: string) =>
      fetchApi<RouteWithClientsData>(`/api/v1/routes/${id}`),

    getByCode: (routeCode: string) =>
      fetchApi<RouteWithClientsData>(`/api/v1/routes/by-code/${routeCode}`),

    create: (data: { routeCode: string; routeType?: 'driving' | 'walking'; employeeId: string; employeeName?: string }) =>
      fetchApi<RouteData>('/api/v1/routes', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (id: string, data: { routeCode?: string; routeType?: 'driving' | 'walking'; employeeId?: string; employeeName?: string }) =>
      fetchApi<RouteData>(`/api/v1/routes/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    delete: (id: string) =>
      fetchApi<{ id: string; deleted: boolean }>(`/api/v1/routes/${id}`, {
        method: 'DELETE',
      }),

    recalculate: (id: string) =>
      fetchApi<RouteData>(`/api/v1/routes/${id}/recalculate`, {
        method: 'POST',
      }),

    /** Bulk update stop order for clients on a route */
    reorderStops: (id: string, stopOrders: { clientId: string; stopOrder: number }[]) =>
      fetchApi<{
        routeId: string;
        routeCode: string;
        updatedCount: number;
        clients: { id: string; clientName: string; stopOrder: number }[];
      }>(`/api/v1/routes/${id}/reorder-stops`, {
        method: 'POST',
        body: JSON.stringify({ stopOrders }),
      }),
  },

  // Route Optimization
  routeOptimization: {
    calculateTime: (routeId: string) =>
      fetchApi<RouteTimeResult>('/api/v1/route-optimization/calculate-time', {
        method: 'POST',
        body: JSON.stringify({ routeId }),
      }),

    optimize: (routeId: string) =>
      fetchApi<RouteOptimizeResult>('/api/v1/route-optimization/optimize', {
        method: 'POST',
        body: JSON.stringify({ routeId }),
      }),
  },

  // Route History
  routeHistory: {
    list: (filters?: {
      limit?: number;
      offset?: number;
      status?: string;
      publishedBy?: string;
      startDate?: string;
      endDate?: string;
      search?: string;
    }) => {
      const params = new URLSearchParams();
      if (filters?.limit) params.set('limit', String(filters.limit));
      if (filters?.offset) params.set('offset', String(filters.offset));
      if (filters?.status) params.set('status', filters.status);
      if (filters?.publishedBy) params.set('publishedBy', filters.publishedBy);
      if (filters?.startDate) params.set('startDate', filters.startDate);
      if (filters?.endDate) params.set('endDate', filters.endDate);
      if (filters?.search) params.set('search', filters.search);
      const query = params.toString() ? `?${params.toString()}` : '';
      return fetchApi<{ data: RouteHistoryEntry[]; meta: { total: number; limit: number; offset: number } }>(`/api/v1/route-history${query}`);
    },

    /** Absolute URL of the CSV export (must be fetched with an auth header, not opened in a tab). */
    getExportUrl: (filters?: { startDate?: string; endDate?: string }) => {
      const params = new URLSearchParams();
      params.set('format', 'csv');
      if (filters?.startDate) params.set('startDate', filters.startDate);
      if (filters?.endDate) params.set('endDate', filters.endDate);
      return `${API_BASE}/api/v1/route-history/export?${params.toString()}`;
    },

    rollback: (draftId: string, data: { effectiveDate?: string; rollbackReason?: string }) =>
      fetchApi<RouteRollbackResult>(`/api/v1/route-drafts/${draftId}/rollback`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  },

  // Route Drafts CRUD
  routeDrafts: {
    list: (status?: string) => {
      const params = new URLSearchParams();
      if (status) params.set('status', status);
      const query = params.toString() ? `?${params.toString()}` : '';
      return fetchApi<RouteDraft[]>(`/api/v1/route-drafts${query}`);
    },

    getById: (id: string) =>
      fetchApi<RouteDraft>(`/api/v1/route-drafts/${id}`),

    create: (data: { name: string; description?: string }) =>
      fetchApi<RouteDraft>('/api/v1/route-drafts', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (id: string, data: { name?: string; description?: string; changes?: RouteDraftChange[]; effectiveDate?: string }) =>
      fetchApi<RouteDraft>(`/api/v1/route-drafts/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    publish: (id: string, data: { effectiveDate?: string }) =>
      fetchApi<RouteDraftPublishResult>(`/api/v1/route-drafts/${id}/publish`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    discard: (id: string) =>
      fetchApi<{ id: string; status: string }>(`/api/v1/route-drafts/${id}`, {
        method: 'DELETE',
      }),
  },

  // Operations Activity
  operationsActivity: {
    list: (filters?: OperationsActivityFilters) => {
      const params = new URLSearchParams();
      if (filters?.entityType) params.set('entityType', filters.entityType);
      if (filters?.status) params.set('status', filters.status);
      if (filters?.limit) params.set('limit', String(filters.limit));
      if (filters?.offset) params.set('offset', String(filters.offset));
      const query = params.toString() ? `?${params.toString()}` : '';
      return fetchApi<OperationsActivityEntry[]>(`/api/v1/operations-activity${query}`);
    },

    getDrafts: () =>
      fetchApi<OperationsActivityEntry[]>('/api/v1/operations-activity/drafts'),

    getPending: () =>
      fetchApi<OperationsActivityEntry[]>('/api/v1/operations-activity/pending'),
  },

  // Clients (unassigned)
  unassignedClients: {
    list: () => fetchApi<UnassignedClient[]>('/api/v1/clients/unassigned'),
  },

  // Route Health
  routeHealth: {
    getSummary: () => fetchApi<RouteHealthSummary>('/api/v1/routes/health/summary'),
  },

  // Audit Log
  auditLog: {
    list: (filters?: AuditLogFilters) => {
      const params = new URLSearchParams();
      if (filters?.publishType) params.set('publishType', filters.publishType);
      if (filters?.limit) params.set('limit', String(filters.limit));
      if (filters?.offset) params.set('offset', String(filters.offset));
      const query = params.toString() ? `?${params.toString()}` : '';
      return fetchApi<PublishEvent[]>(`/api/v1/audit-log${query}`);
    },

    getByPublishId: (publishId: string) =>
      fetchApi<PublishEventDetail>(`/api/v1/audit-log/${publishId}`),

    rollback: (publishId: string) =>
      fetchApi<RollbackResult>(`/api/v1/audit-log/${publishId}/rollback`, {
        method: 'POST',
        body: JSON.stringify({}),
      }),
  },

  // Inventory API (Phase 6)
  inventory: {
    getClientInventory: (clientId: string) =>
      fetchApi<ClientInventory>(`/api/v1/inventory/clients/${clientId}`),

    getContainer: (id: string) =>
      fetchApi<InventoryContainer>(`/api/v1/inventory/containers/${id}`),

    createContainer: (data: {
      clientId: string;
      location: string;
      containerModel?: string;
      containerType?: string;
      containerSize?: string;
      containerMaterial?: string;
      containerColor?: string;
      quantity?: number;
      notes?: string;
      clientName?: string | null;
    }) =>
      fetchApi<InventoryContainer>('/api/v1/inventory/containers', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    updateContainer: (id: string, data: Partial<InventoryContainer> & {
      clientName?: string | null;
    }) =>
      fetchApi<InventoryContainer>(`/api/v1/inventory/containers/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    createPlant: (data: {
      containerId: string;
      plantType: string;
      plantSize?: string;
      quantity?: number;
      position?: string;
      notes?: string;
      healthStatus?: string;
      wholesaleValue?: number;
      catalogItemId?: number;
      clientName?: string | null;
    }) =>
      fetchApi<InventoryPlant>('/api/v1/inventory/plants', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    updatePlant: (id: string, data: Partial<InventoryPlant> & {
      clientName?: string | null;
    }) =>
      fetchApi<InventoryPlant>(`/api/v1/inventory/plants/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    getPlant: (id: string) =>
      fetchApi<InventoryPlantWithContext>(`/api/v1/inventory/plants/${id}`),

    searchPlants: (clientId: string, query?: string) => {
      const params = new URLSearchParams({ clientId });
      if (query) params.set('query', query);
      return fetchApi<InventoryPlantSearchResult[]>(`/api/v1/inventory/plants/search?${params.toString()}`);
    },

    listChangeRequests: (filters?: { status?: string; clientId?: string; limit?: number; offset?: number }) => {
      const params = new URLSearchParams();
      if (filters?.status) params.set('status', filters.status);
      if (filters?.clientId) params.set('clientId', filters.clientId);
      if (filters?.limit) params.set('limit', String(filters.limit));
      if (filters?.offset) params.set('offset', String(filters.offset));
      const query = params.toString() ? `?${params.toString()}` : '';
      return fetchApi<InventoryChangeRequest[]>(`/api/v1/inventory/change-requests${query}`);
    },

    createChangeRequest: (data: {
      clientId: string;
      clientName?: string;
      changeType: string;
      containerId?: string;
      plantId?: string;
      previousValue?: unknown;
      newValue?: unknown;
      description?: string;
      photoIds?: string[];
      sourceType?: string;
      sourceId?: string;
    }) =>
      fetchApi<InventoryChangeRequest>('/api/v1/inventory/change-requests', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    approveChangeRequest: (id: number, data: {
      reviewNotes?: string;
      applyPricing?: boolean;
      applyTime?: boolean;
    }) =>
      fetchApi<InventoryChangeRequest>(`/api/v1/inventory/change-requests/${id}/approve`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    rejectChangeRequest: (id: number, data: {
      reviewNotes?: string;
    }) =>
      fetchApi<InventoryChangeRequest>(`/api/v1/inventory/change-requests/${id}/reject`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    listRequests: (filters?: { status?: string; assignedTo?: string; clientId?: string }) => {
      const params = new URLSearchParams();
      if (filters?.status) params.set('status', filters.status);
      if (filters?.assignedTo) params.set('assignedTo', filters.assignedTo);
      if (filters?.clientId) params.set('clientId', filters.clientId);
      const query = params.toString() ? `?${params.toString()}` : '';
      return fetchApi<InventoryRequest[]>(`/api/v1/inventory/requests${query}`);
    },

    createRequest: (data: {
      requestType: 'verify' | 'create' | 'update_photos' | 'investigate';
      clientId: string;
      clientName?: string;
      assignedTo?: string;
      assignedToName?: string;
      priority?: string;
      notes?: string;
      requirePhotos?: boolean;
      dueDate?: string;
    }) =>
      fetchApi<InventoryRequest>('/api/v1/inventory/requests', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    completeRequest: (id: number, data: {
      completionNotes?: string;
      changeRequestId?: number;
    }) =>
      fetchApi<InventoryRequest>(`/api/v1/inventory/requests/${id}/complete`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    listImports: (filters?: { clientId?: string; limit?: number; offset?: number }) => {
      const params = new URLSearchParams();
      if (filters?.clientId) params.set('clientId', filters.clientId);
      if (filters?.limit) params.set('limit', String(filters.limit));
      if (filters?.offset) params.set('offset', String(filters.offset));
      const query = params.toString() ? `?${params.toString()}` : '';
      return fetchApi<InventoryImportsResponse>(`/api/v1/inventory/imports${query}`);
    },

    getImport: (id: number) =>
      fetchApi<InventoryImport>(`/api/v1/inventory/imports/${id}`),

    // Photos API
    getPhotos: (entityType: 'container' | 'plant' | 'location', entityId: string) =>
      fetchApi<InventoryPhoto[]>(`/api/v1/inventory/photos/${entityType}/${entityId}`),

    createPhoto: (data: {
      entityType: 'container' | 'plant' | 'location';
      entityId: string;
      url: string;
      thumbnailUrl?: string;
      isPrimary?: boolean;
      notes?: string;
    }) =>
      fetchApi<InventoryPhoto>('/api/v1/inventory/photos', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    setPhotoPrimary: (id: string) =>
      fetchApi<InventoryPhoto>(`/api/v1/inventory/photos/${id}/primary`, {
        method: 'PATCH',
      }),

    deletePhoto: (id: string) =>
      fetchApi<{ id: string; deleted: boolean }>(`/api/v1/inventory/photos/${id}`, {
        method: 'DELETE',
      }),

    // Revision review endpoints
    getPendingReviews: () =>
      fetchApi<PendingInventoryReview[]>('/api/v1/inventory/pending-reviews'),

    acknowledgeChanges: (clientId: string) =>
      fetchApi<{ acknowledged: number }>(`/api/v1/inventory/clients/${clientId}/acknowledge-changes`, {
        method: 'POST',
      }),

    submitReview: (clientId: string) =>
      fetchApi<{ submitted: boolean }>(`/api/v1/inventory/clients/${clientId}/submit-review`, {
        method: 'POST',
      }),

    // Migration API
    migration: {
      getQueue: (filters?: { status?: string; search?: string; limit?: number; offset?: number; branchId?: string }) => {
        const params = new URLSearchParams();
        if (filters?.status) params.set('status', filters.status);
        if (filters?.search) params.set('search', filters.search);
        if (filters?.limit) params.set('limit', String(filters.limit));
        if (filters?.offset) params.set('offset', String(filters.offset));
        // Add branch filter (uses store's current branch)
        addBranchParam(params);
        const query = params.toString() ? `?${params.toString()}` : '';
        return fetchApi<MigrationQueue>(`/api/v1/inventory/migration/queue${query}`);
      },

      getClient: (clientId: string) =>
        fetchApi<MigrationClientData>(`/api/v1/inventory/migration/${clientId}`),

      fetchDoc: (docUrl: string) =>
        fetchApi<MigrationDocContent>('/api/v1/inventory/migration/fetch-doc', {
          method: 'POST',
          body: JSON.stringify({ docUrl }),
        }),

      parse: (clientId: string, docContent: string) =>
        fetchApi<MigrationParseResult>('/api/v1/inventory/migration/parse', {
          method: 'POST',
          body: JSON.stringify({ clientId, docContent }),
        }),

      save: (data: {
        clientId: string;
        containers: MigrationParsedContainer[];
        markComplete?: boolean;
      }) =>
        fetchApi<MigrationSaveResult>('/api/v1/inventory/migration/save', {
          method: 'POST',
          body: JSON.stringify(data),
        }),

      skip: (clientId: string, reason?: string) =>
        fetchApi<{ skipped: boolean }>(`/api/v1/inventory/migration/${clientId}/skip`, {
          method: 'PATCH',
          body: JSON.stringify({ reason }),
        }),

      generatePrompt: (trainingExamples: MigrationTrainingExample[]) =>
        fetchApi<MigrationGeneratedPrompt>('/api/v1/inventory/migration/generate-prompt', {
          method: 'POST',
          body: JSON.stringify({ trainingExamples }),
        }),

      batch: (data: {
        clientIds: string[];
      }) =>
        fetchApi<MigrationBatchResult>('/api/v1/inventory/migration/batch', {
          method: 'POST',
          body: JSON.stringify(data),
          timeoutMs: 300000, // 5 minutes for batch
        }),

      getStats: () => {
        const params = new URLSearchParams();
        addBranchParam(params);
        const query = params.toString() ? `?${params.toString()}` : '';
        return fetchApi<MigrationStats>(`/api/v1/inventory/migration/stats${query}`);
      },
    },
  },

  // QA Reports API (Phase 7)
  qaReports: {
    list: (filters?: { clientId?: string; technicianId?: string; startDate?: string; endDate?: string; limit?: number; offset?: number }) => {
      const params = new URLSearchParams();
      if (filters?.clientId) params.set('clientId', filters.clientId);
      if (filters?.technicianId) params.set('technicianId', filters.technicianId);
      if (filters?.startDate) params.set('startDate', filters.startDate);
      if (filters?.endDate) params.set('endDate', filters.endDate);
      if (filters?.limit) params.set('limit', String(filters.limit));
      if (filters?.offset) params.set('offset', String(filters.offset));
      const query = params.toString() ? `?${params.toString()}` : '';
      return fetchApi<QAReport[]>(`/api/v1/qa-reports${query}`);
    },

    getByClient: (clientId: string, limit = 20) =>
      fetchApi<QAReport[]>(`/api/v1/qa-reports/by-client/${clientId}?limit=${limit}`),

    getByTechnician: (technicianId: string, limit = 20) =>
      fetchApi<QAReport[]>(`/api/v1/qa-reports/by-technician/${technicianId}?limit=${limit}`),

    getPending: (filters?: { technicianId?: string; limit?: number }) => {
      const params = new URLSearchParams();
      if (filters?.technicianId) params.set('technicianId', filters.technicianId);
      if (filters?.limit) params.set('limit', String(filters.limit));
      const query = params.toString() ? `?${params.toString()}` : '';
      return fetchApi<QAReport[]>(`/api/v1/qa-reports/pending${query}`);
    },

    get: (id: number) =>
      fetchApi<QAReport>(`/api/v1/qa-reports/${id}`),

    create: (data: {
      clientId: string;
      clientName?: string;
      technicianId?: string;
      technicianName?: string;
      submissionDate?: string;
      scores?: QAScores;
      averageScore?: number;
      inventoryAccurate?: boolean;
      salesOpportunity?: boolean;
      reviewerNotes?: string;
      actionsRequired?: string;
    }) =>
      fetchApi<QAReport>('/api/v1/qa-reports', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (id: number, data: {
      scores?: QAScores;
      averageScore?: number;
      inventoryAccurate?: boolean;
      salesOpportunity?: boolean;
      reviewerNotes?: string;
      actionsRequired?: string;
      photoMetadata?: {
        labels?: string[];
        migratedFrom?: 'jotform' | 'native';
        migratedAt?: string;
        originalPhotoCount?: number;
        rotations?: Record<number, 0 | 90 | 180 | 270>;
      };
    }) =>
      fetchApi<QAReport>(`/api/v1/qa-reports/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    delete: (id: number) =>
      fetchApi<{ id: number; deleted: boolean }>(`/api/v1/qa-reports/${id}`, {
        method: 'DELETE',
      }),

    getStats: (filters?: { startDate?: string; endDate?: string; technicianId?: string }) => {
      const params = new URLSearchParams();
      if (filters?.startDate) params.set('startDate', filters.startDate);
      if (filters?.endDate) params.set('endDate', filters.endDate);
      if (filters?.technicianId) params.set('technicianId', filters.technicianId);
      const query = params.toString() ? `?${params.toString()}` : '';
      return fetchApi<QAStats>(`/api/v1/qa-reports/stats/summary${query}`);
    },

    getRecommendations: (filters?: { limit?: number; minDaysSinceQA?: number; branchId?: string }) => {
      const params = new URLSearchParams();
      if (filters?.limit) params.set('limit', filters.limit.toString());
      if (filters?.minDaysSinceQA) params.set('minDaysSinceQA', filters.minDaysSinceQA.toString());
      if (filters?.branchId) params.set('branchId', filters.branchId);
      const query = params.toString() ? `?${params.toString()}` : '';
      return fetchApi<QARecommendationsByArea[]>(`/api/v1/qa-reports/recommendations${query}`);
    },
  },

  // Alert Dismissals API
  alerts: {
    /** Dismiss an alert with reason */
    dismiss: (data: DismissAlertInput) =>
      fetchApi<{ id: number; dataHash: string; expiresAt: string | null }>('/api/v1/alerts/dismiss', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    /** Get alert type configurations */
    getAlertTypes: () =>
      fetchApi<Record<string, { category: string; allowOngoing: boolean; expireDays: number | null }>>('/api/v1/alerts/types'),

    /** Get dismissals for an entity */
    getDismissals: (filters?: { entityType?: string; entityId?: string; alertType?: string }) => {
      const params = new URLSearchParams();
      if (filters?.entityType) params.set('entityType', filters.entityType);
      if (filters?.entityId) params.set('entityId', filters.entityId);
      if (filters?.alertType) params.set('alertType', filters.alertType);
      const query = params.toString() ? `?${params.toString()}` : '';
      return fetchApi<AlertDismissal[]>(`/api/v1/alerts/dismissals${query}`);
    },

    /** Undo a dismissal */
    undoDismissal: (id: number) =>
      fetchApi<{ id: number }>(`/api/v1/alerts/dismissals/${id}`, {
        method: 'DELETE',
      }),

    /** Check if an alert is currently dismissed */
    checkDismissed: (alertType: string, entityType: string, entityId: string) => {
      const params = new URLSearchParams({ alertType, entityType, entityId });
      return fetchApi<{
        isDismissed: boolean;
        currentHash: string;
        dismissal: AlertDismissal | null;
      }>(`/api/v1/alerts/dismissals/check?${params.toString()}`);
    },

    /** Get all exceptions for the Exceptions Report */
    getExceptions: (filters?: {
      dismissType?: DismissType;
      alertType?: AlertType;
      branchId?: string;
      limit?: number;
      offset?: number;
    }) => {
      const params = new URLSearchParams();
      if (filters?.dismissType) params.set('dismissType', filters.dismissType);
      if (filters?.alertType) params.set('alertType', filters.alertType);
      if (filters?.branchId) params.set('branchId', filters.branchId);
      if (filters?.limit) params.set('limit', String(filters.limit));
      if (filters?.offset) params.set('offset', String(filters.offset));
      addBranchParam(params);
      const query = params.toString() ? `?${params.toString()}` : '';
      return fetchApi<(AlertDismissal & {
        clientName: string | null;
        businessName: string | null;
        branchId: string;
      })[]>(`/api/v1/alerts/exceptions${query}`);
    },
  },

  // Daily Reports API
  dailyReports: {
    /** Get daily report for a specific date (live for recent, stored for old) */
    get: (date: string) =>
      fetchApi<DailyReport>(`/api/v1/daily-reports/${date}`),

    /** Force generate and store a report for a date */
    generate: (date: string) =>
      fetchApi<DailyReport>(`/api/v1/daily-reports/${date}/generate`, {
        method: 'POST',
      }),

    /** List all stored reports */
    list: (filters?: { limit?: number; offset?: number }) => {
      const params = new URLSearchParams();
      if (filters?.limit) params.set('limit', String(filters.limit));
      if (filters?.offset) params.set('offset', String(filters.offset));
      const query = params.toString() ? `?${params.toString()}` : '';
      return fetchApi<DailyReportSummary[]>(`/api/v1/daily-reports${query}`);
    },
  },

  // Settings API
  settings: {
    /** Get service regions for suspicious location detection */
    getServiceRegions: (branchId?: string) => {
      const params = new URLSearchParams();
      if (branchId) params.set('branchId', branchId);
      const query = params.toString();
      return fetchApi<{
        regions: ServiceRegion[];
        branchId: string;
        isDefault: boolean;
        isFallback: boolean;
      }>(`/api/v1/settings/map/service-regions${query ? `?${query}` : ''}`);
    },

    /** Update all service regions (full replacement) */
    updateServiceRegions: (regions: ServiceRegion[], branchId?: string) => {
      const params = new URLSearchParams();
      if (branchId) params.set('branchId', branchId);
      const query = params.toString();
      return fetchApi<{ regions: ServiceRegion[]; branchId: string }>(
        `/api/v1/settings/map/service-regions${query ? `?${query}` : ''}`,
        {
          method: 'PUT',
          body: JSON.stringify({ regions }),
        }
      );
    },

    /** Add a single service region */
    addServiceRegion: (region: Omit<ServiceRegion, 'color'> & { color?: string }, branchId?: string) => {
      const params = new URLSearchParams();
      if (branchId) params.set('branchId', branchId);
      const query = params.toString();
      return fetchApi<ServiceRegion & { branchId: string }>(
        `/api/v1/settings/map/service-regions${query ? `?${query}` : ''}`,
        {
          method: 'POST',
          body: JSON.stringify(region),
        }
      );
    },

    /** Delete a service region */
    deleteServiceRegion: (regionId: string, branchId?: string) => {
      const params = new URLSearchParams();
      if (branchId) params.set('branchId', branchId);
      const query = params.toString();
      return fetchApi<{ deletedId: string; branchId: string }>(
        `/api/v1/settings/map/service-regions/${regionId}${query ? `?${query}` : ''}`,
        {
          method: 'DELETE',
        }
      );
    },

    // Email Sender Aliases
    /** Get all email sender aliases */
    getEmailAliases: (branchId?: string) => {
      const params = new URLSearchParams();
      if (branchId) params.set('branchId', branchId);
      const query = params.toString();
      return fetchApi<EmailSenderAlias[]>(`/api/v1/settings/email/aliases${query ? `?${query}` : ''}`);
    },

    /** Get a specific email sender alias */
    getEmailAlias: (id: string) =>
      fetchApi<EmailSenderAlias>(`/api/v1/settings/email/aliases/${id}`),

    /** Create a new email sender alias */
    createEmailAlias: (alias: {
      email: string;
      displayName: string;
      description?: string;
      replyTo?: string;
      branchId?: string;
    }) =>
      fetchApi<EmailSenderAlias>('/api/v1/settings/email/aliases', {
        method: 'POST',
        body: JSON.stringify(alias),
      }),

    /** Update an email sender alias */
    updateEmailAlias: (id: string, updates: {
      email?: string;
      displayName?: string;
      description?: string;
      replyTo?: string;
    }) =>
      fetchApi<EmailSenderAlias>(`/api/v1/settings/email/aliases/${id}`, {
        method: 'PUT',
        body: JSON.stringify(updates),
      }),

    /** Delete an email sender alias */
    deleteEmailAlias: (id: string) =>
      fetchApi<{ deletedId: string }>(`/api/v1/settings/email/aliases/${id}`, {
        method: 'DELETE',
      }),

    /** Verify an email sender alias (send test email) */
    verifyEmailAlias: (id: string, recipientEmail?: string) =>
      fetchApi<{
        id: string;
        isVerified: boolean;
        verifiedAt: string;
        testEmailSentTo: string;
        messageId?: string;
      }>(`/api/v1/settings/email/aliases/${id}/verify`, {
        method: 'POST',
        body: JSON.stringify({ recipientEmail }),
      }),

    /** Set an alias as the default for its branch */
    setDefaultEmailAlias: (id: string) =>
      fetchApi<{
        id: string;
        branchId: string;
        isDefault: boolean;
        message: string;
      }>(`/api/v1/settings/email/aliases/${id}/set-default`, {
        method: 'POST',
      }),

    // Automation Email Config
    /** Get automation email configs */
    getAutomationEmailConfigs: (filters?: { automationId?: string; branchId?: string }) => {
      const params = new URLSearchParams();
      if (filters?.automationId) params.set('automationId', filters.automationId);
      if (filters?.branchId) params.set('branchId', filters.branchId);
      const query = params.toString();
      return fetchApi<AutomationEmailConfig[]>(`/api/v1/settings/email/automation-config${query ? `?${query}` : ''}`);
    },

    /** Set sender alias for an automation */
    setAutomationEmailConfig: (automationId: string, config: {
      senderAliasId?: string;
      branchId?: string;
    }) =>
      fetchApi<AutomationEmailConfig>(`/api/v1/settings/email/automation-config/${automationId}`, {
        method: 'PUT',
        body: JSON.stringify(config),
      }),

    /** Clear sender override for an automation (use default) */
    clearAutomationEmailConfig: (automationId: string, branchId?: string) => {
      const params = new URLSearchParams();
      if (branchId) params.set('branchId', branchId);
      const query = params.toString();
      return fetchApi<{ automationId: string; branchId: string }>(
        `/api/v1/settings/email/automation-config/${automationId}${query ? `?${query}` : ''}`,
        { method: 'DELETE' }
      );
    },
  },

  // User Management API
  userManagement: {
    /** Get all workspace users with their linked employee info */
    getWorkspaceUsers: () =>
      fetchApi<WorkspaceUser[]>('/api/v1/user-management/workspace-users'),

    /** Get all invitations */
    getInvitations: () =>
      fetchApi<UserInvitation[]>('/api/v1/user-management/invitations'),

    /** Link an employee to a workspace email */
    linkEmployee: (data: { employeeId: string; workspaceEmail: string }) =>
      fetchApi<{ employeeId: string; workspaceEmail: string }>('/api/v1/user-management/link-employee', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    /** Send an invitation to an employee */
    sendInvitation: (data: { employeeId: string; email: string }) =>
      fetchApi<{ id: number }>('/api/v1/user-management/invitations', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    /** Cancel an invitation */
    cancelInvitation: (invitationId: number) =>
      fetchApi<{ id: number }>(`/api/v1/user-management/invitations/${invitationId}`, {
        method: 'DELETE',
      }),

    /** Resend an invitation */
    resendInvitation: (invitationId: number) =>
      fetchApi<{ id: number }>(`/api/v1/user-management/invitations/${invitationId}/resend`, {
        method: 'POST',
      }),

    /** Accept an invitation (public — no auth required, separate mount) */
    acceptInvitation: (token: string) =>
      fetchApi<{ accepted?: boolean }>('/api/v1/invitations/accept', {
        method: 'POST',
        body: JSON.stringify({ token }),
      }),
  },

  // Catalog / Price List API
  catalog: {
    /** Get catalog stats */
    getStats: () =>
      fetchApi<CatalogStats>(`/api/v1/catalog/stats${catalogBranchQuery()}`),

    /** Get size defaults */
    getSizeDefaults: () =>
      fetchApi<SizeDefault[]>(`/api/v1/catalog/size-defaults${catalogBranchQuery()}`),

    /** Get cost formulas */
    getFormulas: () =>
      fetchApi<CostFormula[]>(`/api/v1/catalog/formulas${catalogBranchQuery()}`),

    /** Get upload history */
    getUploadHistory: () =>
      fetchApi<CatalogUpload[]>(`/api/v1/catalog/uploads${catalogBranchQuery()}`),

    /** Seed size defaults from catalog data */
    seedSizeDefaults: () =>
      fetchApi<SizeDefault[]>('/api/v1/catalog/size-defaults/seed', {
        method: 'POST',
        body: JSON.stringify({ branchId: selectedBranchId() }),
      }),

    /** Seed cost formulas with defaults */
    seedFormulas: () =>
      fetchApi<CostFormula[]>('/api/v1/catalog/formulas/seed', {
        method: 'POST',
        body: JSON.stringify({ branchId: selectedBranchId() }),
      }),

    /** Upload catalog CSV (pre-parsed rows sent as JSON) */
    uploadCsv: (data: { data: Record<string, string>[]; branchId?: string }) =>
      fetchApi<{ uploadId: number; totalRows: number; newItems: number; updatedItems: number; skippedDuplicates: number; errors: number }>('/api/v1/catalog/upload', {
        method: 'POST',
        body: JSON.stringify({ ...data, branchId: data.branchId ?? selectedBranchId() }),
      }),

    /** Download the catalog CSV as text (auth header, error message surfaced, blank body is an error). */
    downloadCsv: () => fetchCsvText(`/api/v1/catalog/download${catalogBranchQuery()}`),

    /** List catalog items */
    list: (params?: { search?: string; size?: string; area?: string; limit?: number; offset?: number }) => {
      const searchParams = new URLSearchParams();
      if (params?.search) searchParams.set('search', params.search);
      if (params?.size) searchParams.set('size', params.size);
      if (params?.area) searchParams.set('area', params.area);
      if (params?.limit) searchParams.set('limit', String(params.limit));
      if (params?.offset) searchParams.set('offset', String(params.offset));
      const query = searchParams.toString();
      return fetchApi<CatalogItem[]>(`/api/v1/catalog${query ? `?${query}` : ''}`);
    },

    /** Get single catalog item */
    getById: (id: number) =>
      fetchApi<CatalogItem>(`/api/v1/catalog/${id}`),

    /** Create a catalog item */
    create: (data: { size?: string; plant?: string; wholesalePrice?: number; floralTag?: string; area?: string; imageUrl?: string }) =>
      fetchApi<CatalogItem>('/api/v1/catalog', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    /** Update a catalog item */
    update: (id: number, data: Partial<{ size: string; plant: string; wholesalePrice: number; floralTag: string; area: string; imageUrl: string }>) =>
      fetchApi<CatalogItem>(`/api/v1/catalog/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    /** Delete a catalog item */
    delete: (id: number) =>
      fetchApi<{ id: number; deleted: boolean }>(`/api/v1/catalog/${id}`, {
        method: 'DELETE',
      }),

    /** Get dropdown options for workorders (with images) */
    getWorkordersDropdown: () =>
      fetchApi<{ id: number; label: string; size: string; plant: string; wholesalePrice: number; imageUrl: string | null }[]>(
        '/api/v1/catalog/workorders-dropdown'
      ),

    /** Get configured Google Sheets spreadsheet */
    getSheetsConfig: () =>
      fetchApi<{ spreadsheetId: string; spreadsheetUrl: string; serviceAccountEmail: string } | null>('/api/v1/catalog/sheets-config'),

    /** Save Google Sheets spreadsheet URL */
    saveSheetsConfig: (spreadsheetUrl: string) =>
      fetchApi<{ spreadsheetId: string; spreadsheetUrl: string; serviceAccountEmail: string }>('/api/v1/catalog/sheets-config', {
        method: 'POST',
        body: JSON.stringify({ spreadsheetUrl }),
      }),

    /** Push all catalog items to Google Sheets */
    pushToSheets: (branchId: string | undefined = selectedBranchId()) =>
      fetchApi<{ rowsWritten: number; spreadsheetUrl: string }>('/api/v1/catalog/push-to-sheets', {
        method: 'POST',
        body: JSON.stringify({ branchId }),
      }),

    /** Pull catalog updates from Google Sheets */
    pullFromSheets: (branchId: string | undefined = selectedBranchId()) =>
      fetchApi<{ totalRows: number; newItems: number; updatedItems: number; skippedDuplicates: number; errors: number }>('/api/v1/catalog/pull-from-sheets', {
        method: 'POST',
        body: JSON.stringify({ branchId }),
      }),
  },

  // Gmail Integration API
  gmail: {
    /** Get OAuth URL to start Gmail connection flow */
    getAuthUrl: () =>
      fetchApi<{ url: string }>('/api/v1/gmail/auth-url'),

    /** Get Gmail connection status */
    getStatus: () =>
      fetchApi<GmailConnectionStatus>('/api/v1/gmail/status'),

    /** Disconnect Gmail */
    disconnect: () =>
      fetchApi<{ disconnected: boolean }>('/api/v1/gmail/disconnect', {
        method: 'DELETE',
      }),

    /** Send an email via Gmail */
    send: (data: SendEmailInput) =>
      fetchApi<SendEmailResult>('/api/v1/gmail/send', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    /** List sent emails */
    listEmails: (options?: { entityType?: string; entityId?: string; limit?: number; offset?: number }) => {
      const params = new URLSearchParams();
      if (options?.entityType) params.set('entityType', options.entityType);
      if (options?.entityId) params.set('entityId', options.entityId);
      if (options?.limit) params.set('limit', String(options.limit));
      if (options?.offset) params.set('offset', String(options.offset));
      const query = params.toString();
      return fetchApi<SentEmail[]>(`/api/v1/gmail/emails${query ? `?${query}` : ''}`);
    },

    /** Get single email details */
    getEmail: (id: string) =>
      fetchApi<SentEmail>(`/api/v1/gmail/emails/${id}`),

    // Template methods
    /** List email templates */
    listTemplates: (params?: { entityType?: string; activeOnly?: boolean }) => {
      const searchParams = new URLSearchParams();
      if (params?.entityType) searchParams.set('entityType', params.entityType);
      if (params?.activeOnly !== undefined) searchParams.set('activeOnly', String(params.activeOnly));
      const query = searchParams.toString();
      return fetchApi<EmailTemplate[]>(`/api/v1/gmail/templates${query ? `?${query}` : ''}`);
    },

    /** Get single template */
    getTemplate: (id: string) =>
      fetchApi<EmailTemplate>(`/api/v1/gmail/templates/${id}`),

    /** Create template */
    createTemplate: (data: CreateEmailTemplateInput) =>
      fetchApi<EmailTemplate>('/api/v1/gmail/templates', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    /** Update template */
    updateTemplate: (id: string, data: Partial<CreateEmailTemplateInput>) =>
      fetchApi<EmailTemplate>(`/api/v1/gmail/templates/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    /** Delete template */
    deleteTemplate: (id: string, hard = false) =>
      fetchApi<{ deleted: boolean }>(`/api/v1/gmail/templates/${id}?hard=${hard}`, {
        method: 'DELETE',
      }),
  },

  // Email Management (Superhuman-style productivity features)
  emails: {
    /** Get a single received email with full body */
    getReceivedEmail: (id: string) =>
      fetchApi<ReceivedEmail>(`/api/v1/emails/received/${id}`),

    /** Get unified inbox (received + sent emails) */
    getInbox: (options?: {
      filter?: 'all' | 'unread' | 'starred' | 'snoozed';
      entityType?: string;
      entityId?: string;
      search?: string;
      limit?: number;
      offset?: number;
    }) => {
      const params = new URLSearchParams();
      if (options?.filter) params.set('filter', options.filter);
      if (options?.entityType) params.set('entity_type', options.entityType);
      if (options?.entityId) params.set('entity_id', options.entityId);
      if (options?.search) params.set('search', options.search);
      if (options?.limit) params.set('limit', String(options.limit));
      if (options?.offset) params.set('offset', String(options.offset));
      const query = params.toString();
      return fetchApi<EmailInboxResponse>(`/api/v1/emails/inbox${query ? `?${query}` : ''}`);
    },

    /** Snooze an email */
    snooze: (emailId: string, snoozeUntil: string) =>
      fetchApi<{ emailId: string; snoozedUntil: string }>(`/api/v1/emails/${emailId}/snooze`, {
        method: 'POST',
        body: JSON.stringify({ snoozeUntil }),
      }),

    /** Unsnooze an email */
    unsnooze: (emailId: string) =>
      fetchApi<{ emailId: string }>(`/api/v1/emails/${emailId}/snooze`, {
        method: 'DELETE',
      }),

    /** Archive an email */
    archive: (emailId: string) =>
      fetchApi<{ emailId: string; archivedAt: string }>(`/api/v1/emails/${emailId}/archive`, {
        method: 'POST',
      }),

    /** Unarchive an email */
    unarchive: (emailId: string) =>
      fetchApi<{ emailId: string }>(`/api/v1/emails/${emailId}/unarchive`, {
        method: 'POST',
      }),

    /** Star an email */
    star: (emailId: string) =>
      fetchApi<{ emailId: string; isStarred: boolean }>(`/api/v1/emails/${emailId}/star`, {
        method: 'POST',
      }),

    /** Unstar an email */
    unstar: (emailId: string) =>
      fetchApi<{ emailId: string; isStarred: boolean }>(`/api/v1/emails/${emailId}/star`, {
        method: 'DELETE',
      }),

    /** Mark email as read */
    markRead: (emailId: string) =>
      fetchApi<{ emailId: string; readAt: string }>(`/api/v1/emails/${emailId}/mark-read`, {
        method: 'POST',
      }),

    /** Mark email as unread */
    markUnread: (emailId: string) =>
      fetchApi<{ emailId: string }>(`/api/v1/emails/${emailId}/mark-unread`, {
        method: 'POST',
      }),

    // Delayed Send
    /** Schedule an email for delayed sending */
    scheduleEmail: (data: {
      to: string | string[];
      subject: string;
      body: string;
      sendAt: string;
      entityType?: string;
      entityId?: string;
    }) =>
      fetchApi<{ id: string; sendAt: string; status: string }>(`/api/v1/emails/delayed`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    /** List scheduled emails */
    listScheduled: (options?: {
      status?: 'scheduled' | 'sent' | 'cancelled' | 'failed';
      limit?: number;
      offset?: number;
    }) => {
      const params = new URLSearchParams();
      if (options?.status) params.set('status', options.status);
      if (options?.limit) params.set('limit', String(options.limit));
      if (options?.offset) params.set('offset', String(options.offset));
      const query = params.toString();
      return fetchApi<ScheduledEmail[]>(`/api/v1/emails/delayed${query ? `?${query}` : ''}`);
    },

    /** Cancel a scheduled email */
    cancelScheduled: (emailId: string) =>
      fetchApi<{ id: string; status: string }>(`/api/v1/emails/delayed/${emailId}`, {
        method: 'DELETE',
      }),

    // Follow-Ups
    /** Set a follow-up reminder on an email */
    setFollowUp: (emailId: string, data: { followUpAt: string; notes?: string }) =>
      fetchApi<{ id: string; followUpAt: string; status: string }>(`/api/v1/emails/${emailId}/follow-up`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    /** Get follow-up for a specific email */
    getFollowUp: (emailId: string) =>
      fetchApi<{ id: string; followUpAt: string; notes?: string; status: string }>(`/api/v1/emails/${emailId}/follow-up`),

    /** List all follow-ups */
    listFollowUps: (options?: {
      status?: 'pending' | 'completed' | 'dismissed';
      limit?: number;
      offset?: number;
    }) => {
      const params = new URLSearchParams();
      if (options?.status) params.set('status', options.status);
      if (options?.limit) params.set('limit', String(options.limit));
      if (options?.offset) params.set('offset', String(options.offset));
      const query = params.toString();
      return fetchApi<EmailFollowUp[]>(`/api/v1/emails/follow-ups${query ? `?${query}` : ''}`);
    },

    /** Update follow-up status (mark as completed or dismissed) */
    updateFollowUpStatus: (followUpId: string, status: 'completed' | 'dismissed') =>
      fetchApi<{ id: string; status: string }>(`/api/v1/emails/follow-ups/${followUpId}`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      }),

    /** Remove a follow-up reminder */
    removeFollowUp: (emailId: string) =>
      fetchApi<{ emailId: string }>(`/api/v1/emails/${emailId}/follow-up`, {
        method: 'DELETE',
      }),

    // Comments
    /** Add a comment to an email */
    addComment: (emailId: string, content: string) =>
      fetchApi<{ id: string; emailId: string; content: string; createdAt: string }>(`/api/v1/emails/${emailId}/comments`, {
        method: 'POST',
        body: JSON.stringify({ content }),
      }),

    /** List comments for an email */
    listComments: (emailId: string) =>
      fetchApi<EmailComment[]>(`/api/v1/emails/${emailId}/comments`),

    /** Delete a comment */
    deleteComment: (commentId: string) =>
      fetchApi<{ id: string }>(`/api/v1/emails/comments/${commentId}`, {
        method: 'DELETE',
      }),
  },

  // QuickBooks Online Integration API
  qbo: {
    /** Get all QBO connections */
    getConnections: () =>
      fetchApi<QboConnection[]>('/api/v1/qbo/connections'),

    /** Get OAuth URL to start QBO connection flow */
    getAuthUrl: () =>
      fetchApi<{ url: string }>('/api/v1/qbo/auth-url'),

    /** Update connection settings (branch mapping, sync enabled) */
    updateConnection: (id: string, data: Partial<{ branchId: string; syncEnabled: boolean }>) =>
      fetchApi<QboConnection>(`/api/v1/qbo/connections/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    /** Disconnect QBO */
    disconnect: (id: string) =>
      fetchApi<{ disconnected: boolean }>(`/api/v1/qbo/connections/${id}`, {
        method: 'DELETE',
      }),

    /** Trigger sync for a connection */
    sync: (connectionId: string) =>
      fetchApi<{ message: string; connectionId: string }>(`/api/v1/qbo/connections/${connectionId}/sync`, {
        method: 'POST',
      }),

    /** Get synced customers */
    getCustomers: (options?: { connectionId?: string; limit?: number; offset?: number }) => {
      const params = new URLSearchParams();
      if (options?.connectionId) params.set('connectionId', options.connectionId);
      if (options?.limit) params.set('limit', String(options.limit));
      if (options?.offset) params.set('offset', String(options.offset));
      const query = params.toString();
      return fetchApi<QboCustomer[]>(`/api/v1/qbo/customers${query ? `?${query}` : ''}`);
    },

    /** Get synced invoices */
    getInvoices: (options?: { connectionId?: string; limit?: number; offset?: number }) => {
      const params = new URLSearchParams();
      if (options?.connectionId) params.set('connectionId', options.connectionId);
      if (options?.limit) params.set('limit', String(options.limit));
      if (options?.offset) params.set('offset', String(options.offset));
      const query = params.toString();
      return fetchApi<QboInvoice[]>(`/api/v1/qbo/invoices${query ? `?${query}` : ''}`);
    },

    /** Get synced payments */
    getPayments: (options?: { connectionId?: string; limit?: number; offset?: number }) => {
      const params = new URLSearchParams();
      if (options?.connectionId) params.set('connectionId', options.connectionId);
      if (options?.limit) params.set('limit', String(options.limit));
      if (options?.offset) params.set('offset', String(options.offset));
      const query = params.toString();
      return fetchApi<QboPayment[]>(`/api/v1/qbo/payments${query ? `?${query}` : ''}`);
    },

    /** Get sync history */
    getSyncLog: (options?: { connectionId?: string; limit?: number }) => {
      const params = new URLSearchParams();
      if (options?.connectionId) params.set('connectionId', options.connectionId);
      if (options?.limit) params.set('limit', String(options.limit));
      const query = params.toString();
      return fetchApi<QboSyncLogEntry[]>(`/api/v1/qbo/sync-log${query ? `?${query}` : ''}`);
    },

    /**
     * Get invoice PDF URL.
     * Returns a URL to fetch the PDF from QBO through our backend.
     */
    getInvoicePdfUrl: (qboInvoiceId: string) => {
      return `${API_BASE}/api/v1/qbo/invoices/${qboInvoiceId}/pdf`;
    },

    // QBO Customer <-> Client Mapping

    /** Get clients linked to a QBO customer */
    getLinkedClients: (qboCustomerId: string) =>
      fetchApi<{
        id: string;
        clientName: string;
        businessName: string | null;
        contactEmail: string | null;
        contactPhone: string | null;
        branchId: string;
        isInactive: boolean;
      }[]>(`/api/v1/qbo/customers/${qboCustomerId}/clients`),

    /** Get unmapped QBO customers with fuzzy match suggestions */
    getUnmappedCustomers: (options?: { search?: string; limit?: number }) => {
      const params = new URLSearchParams();
      if (options?.search) params.set('search', options.search);
      if (options?.limit) params.set('limit', String(options.limit));
      const query = params.toString();
      return fetchApi<(QboCustomer & {
        suggestedClients: {
          id: string;
          clientName: string;
          businessName: string | null;
          matchScore: number;
        }[];
      })[]>(`/api/v1/qbo/customers/unmapped${query ? `?${query}` : ''}`);
    },

    /** Link one or more clients to a QBO customer */
    linkClients: (qboCustomerId: string, clientIds: string[]) =>
      fetchApi<{
        qboCustomerId: string;
        qboCustomerName: string;
        linkedClientIds: string[];
        linkedCount: number;
      }>(`/api/v1/qbo/customers/${qboCustomerId}/link`, {
        method: 'POST',
        body: JSON.stringify({ clientIds }),
      }),

    /** Unlink a client from a QBO customer */
    unlinkClient: (qboCustomerId: string, clientId: string) =>
      fetchApi<{ unlinkedClientId: string }>(
        `/api/v1/qbo/customers/${qboCustomerId}/unlink/${clientId}`,
        { method: 'DELETE' }
      ),
  },

  // Finance Module API (Collections, Aging)
  finance: {
    /** Get dashboard stats (aging summary, collections count, etc.) */
    getDashboard: (options?: { branchId?: string }) => {
      const params = new URLSearchParams();
      if (options?.branchId) params.set('branchId', options.branchId);
      const query = params.toString();
      return fetchApi<FinanceDashboardData>(`/api/v1/finance/dashboard${query ? `?${query}` : ''}`);
    },

    /** List collections with filters */
    getCollections: (options?: {
      status?: string;
      priority?: string;
      assignedTo?: string;
      branchId?: string;
      hasFollowup?: boolean;
      limit?: number;
      offset?: number;
    }) => {
      const params = new URLSearchParams();
      if (options?.status) params.set('status', options.status);
      if (options?.priority) params.set('priority', options.priority);
      if (options?.assignedTo) params.set('assignedTo', options.assignedTo);
      if (options?.branchId) params.set('branchId', options.branchId);
      if (options?.hasFollowup) params.set('hasFollowup', 'true');
      if (options?.limit) params.set('limit', String(options.limit));
      if (options?.offset) params.set('offset', String(options.offset));
      const query = params.toString();
      return fetchApi<FinanceCollection[]>(`/api/v1/finance/collections${query ? `?${query}` : ''}`);
    },

    /** Get single collection with details */
    getCollection: (id: number) =>
      fetchApi<FinanceCollectionDetail>(`/api/v1/finance/collections/${id}`),

    /** Create new collection */
    createCollection: (data: CreateCollectionInput) =>
      fetchApi<FinanceCollection>('/api/v1/finance/collections', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    /** Update collection */
    updateCollection: (id: number, data: UpdateCollectionInput) =>
      fetchApi<FinanceCollection>(`/api/v1/finance/collections/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    /** Log outreach attempt */
    logOutreach: (collectionId: number, data: CreateOutreachInput) =>
      fetchApi<FinanceOutreach>(`/api/v1/finance/collections/${collectionId}/outreach`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    /** Update an outreach entry (notes, outcome, follow-up) */
    updateOutreach: (
      collectionId: number,
      outreachId: number,
      data: {
        notes?: string;
        outcome?: string;
        followupRequired?: boolean;
        followupDate?: string;
        followupNotes?: string;
      }
    ) =>
      fetchApi<FinanceOutreach>(`/api/v1/finance/collections/${collectionId}/outreach/${outreachId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    /** Get outreach history for a collection */
    getOutreach: (collectionId: number, options?: { limit?: number }) => {
      const params = new URLSearchParams();
      if (options?.limit) params.set('limit', String(options.limit));
      const query = params.toString();
      return fetchApi<FinanceOutreach[]>(`/api/v1/finance/collections/${collectionId}/outreach${query ? `?${query}` : ''}`);
    },

    /** Get current aging report with client breakdown */
    getAgingReport: (options?: { branchId?: string }) => {
      const params = new URLSearchParams();
      if (options?.branchId) params.set('branchId', options.branchId);
      const query = params.toString();
      return fetchApi<FinanceAgingReport>(`/api/v1/finance/aging${query ? `?${query}` : ''}`);
    },

    /** Get historical aging snapshots */
    getAgingHistory: (options?: { branchId?: string; limit?: number }) => {
      const params = new URLSearchParams();
      if (options?.branchId) params.set('branchId', options.branchId);
      if (options?.limit) params.set('limit', String(options.limit));
      const query = params.toString();
      return fetchApi<FinanceAgingSnapshot[]>(`/api/v1/finance/aging/history${query ? `?${query}` : ''}`);
    },

    /** Create aging snapshot */
    createAgingSnapshot: (options?: { branchId?: string }) =>
      fetchApi<FinanceAgingSnapshot>('/api/v1/finance/aging/snapshot', {
        method: 'POST',
        body: JSON.stringify(options || {}),
      }),

    /** Auto-create collections from overdue invoices */
    autoCreateCollections: (options?: { branchId?: string; minDaysOverdue?: number }) =>
      fetchApi<{ created: number; customersChecked: number; existingCollections: number }>(
        '/api/v1/finance/collections/auto-create',
        {
          method: 'POST',
          body: JSON.stringify(options || {}),
        }
      ),

    /** Get collection tab counts for workflow tabs */
    getTabCounts: async (options?: { branchId?: string }) => {
      const params = new URLSearchParams();
      if (options?.branchId) params.append('branchId', options.branchId);
      return fetchApi<CollectionTabCounts>(`/api/v1/finance/collections/tab-counts?${params}`);
    },

    /** Get recently paid invoices (from QBO payments) */
    getRecentlyPaid: async (options?: { branchId?: string; days?: number }) => {
      const params = new URLSearchParams();
      if (options?.branchId) params.append('branchId', options.branchId);
      if (options?.days) params.append('days', String(options.days));
      const query = params.toString();
      return fetchApi<RecentlyPaidData>(`/api/v1/finance/recently-paid${query ? `?${query}` : ''}`);
    },

    /** Get unbilled billable workorders (To Bill tab) */
    getUnbilledWorkorders: async (options?: { branchId?: string }) => {
      const params = new URLSearchParams();
      if (options?.branchId) params.append('branchId', options.branchId);
      const query = params.toString();
      return fetchApi<UnbilledWorkordersData>(`/api/v1/finance/unbilled-workorders${query ? `?${query}` : ''}`);
    },

    /** Get unsent invoices (To Send tab) */
    getUnsentInvoices: async (options?: { branchId?: string }) => {
      const params = new URLSearchParams();
      if (options?.branchId) params.append('branchId', options.branchId);
      const query = params.toString();
      return fetchApi<UnsentInvoicesData>(`/api/v1/finance/unsent-invoices${query ? `?${query}` : ''}`);
    },

    /** Get finance module settings */
    getSettings: () => fetchApi<FinanceSettings>('/api/v1/finance/settings'),

    /** Update finance module settings */
    updateSettings: (settings: Partial<FinanceSettings>) =>
      fetchApi<FinanceSettings>('/api/v1/finance/settings', {
        method: 'PATCH',
        body: JSON.stringify(settings),
      }),

    /** Get comprehensive collections analytics */
    getAnalytics: (params: FinanceAnalyticsParams) => {
      const queryParams = new URLSearchParams();
      queryParams.set('from', params.from);
      queryParams.set('to', params.to);
      if (params.branchId) queryParams.set('branchId', params.branchId);
      const query = queryParams.toString();
      return fetchApi<FinanceAnalytics>(`/api/v1/finance/analytics?${query}`);
    },

    /** Download the collections CSV as text (auth header, error message surfaced, blank body is an error). */
    exportCollectionsCsv: (options?: { tab?: string; branchId?: string }) =>
      fetchCsvText(collectionsExportPath(options)),

    // Collection Touches API (Issue #3)
    /** Get collection touches for a customer */
    getTouches: (qboCustomerId: string, options?: { limit?: number }) => {
      const params = new URLSearchParams();
      params.set('qboCustomerId', qboCustomerId);
      if (options?.limit) params.set('limit', String(options.limit));
      const query = params.toString();
      return fetchApi<CollectionTouch[]>(`/api/v1/finance/touches?${query}`);
    },

    /** Create a new collection touch */
    createTouch: (data: CreateCollectionTouchInput) =>
      fetchApi<CollectionTouch>('/api/v1/finance/touches', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    /** Get customer collections config */
    getCustomerConfig: (qboCustomerId: string) =>
      fetchApi<CustomerCollectionsConfig>(`/api/v1/finance/customers/${qboCustomerId}/config`),

    /** Update customer collections config */
    updateCustomerConfig: (qboCustomerId: string, data: UpdateCustomerConfigInput) =>
      fetchApi<CustomerCollectionsConfig>(`/api/v1/finance/customers/${qboCustomerId}/config`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    /** Get full customer profile with invoices, payments, touches, and aging */
    getCustomerProfile: (qboCustomerId: string) =>
      fetchApi<CustomerProfile>(`/api/v1/finance/customers/${qboCustomerId}/profile`),

    // Invoice-Aware Collection Email API
    /** Send a collection email with invoice selection, PDF attachments, and auto-touch creation */
    sendCollectionEmail: (data: SendCollectionEmailInput) =>
      fetchApi<SendCollectionEmailResult>('/api/v1/finance/collection-email/send', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    /** Get sent invoice emails for a customer */
    getInvoiceEmails: (qboCustomerId: string, options?: { limit?: number }) => {
      const params = new URLSearchParams();
      params.set('qboCustomerId', qboCustomerId);
      if (options?.limit) params.set('limit', String(options.limit));
      const query = params.toString();
      return fetchApi<InvoiceEmail[]>(`/api/v1/finance/invoice-emails?${query}`);
    },

    /** Get received emails for a customer (matched via tracking codes) */
    getReceivedEmails: (qboCustomerId: string, options?: { limit?: number }) => {
      const params = new URLSearchParams();
      params.set('qboCustomerId', qboCustomerId);
      if (options?.limit) params.set('limit', String(options.limit));
      const query = params.toString();
      return fetchApi<ReceivedEmail[]>(`/api/v1/finance/received-emails?${query}`);
    },

    /** Get email thread (sent + received) for a customer */
    getEmailThread: (qboCustomerId: string) =>
      fetchApi<EmailThreadResponse>(`/api/v1/finance/customers/${qboCustomerId}/email-thread`),
  },

  // CRM Module API (Companies, Contacts, Deals, Pipeline)
  crm: {
    /** Get CRM dashboard data with metrics */
    getDashboard: () => fetchApi<CrmDashboardData>('/api/v1/crm/dashboard'),

    // Companies
    /** List companies with filters */
    getCompanies: (options?: {
      search?: string;
      status?: string;
      tier?: string;
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
      limit?: number;
      offset?: number;
    }) => {
      const params = new URLSearchParams();
      if (options?.search) params.set('search', options.search);
      if (options?.status) params.set('status', options.status);
      if (options?.tier) params.set('tier', options.tier);
      if (options?.sortBy) params.set('sortBy', options.sortBy);
      if (options?.sortOrder) params.set('sortOrder', options.sortOrder);
      if (options?.limit) params.set('limit', String(options.limit));
      if (options?.offset) params.set('offset', String(options.offset));
      const query = params.toString();
      return fetchApi<CrmCompany[]>(`/api/v1/crm/companies${query ? `?${query}` : ''}`);
    },

    /** Get single company with details */
    getCompany: (id: string) => fetchApi<CrmCompanyDetail>(`/api/v1/crm/companies/${id}`),

    /** Create new company */
    createCompany: (data: CreateCrmCompanyInput) =>
      fetchApi<CrmCompany>('/api/v1/crm/companies', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    /** Update company */
    updateCompany: (id: string, data: Partial<CreateCrmCompanyInput>) =>
      fetchApi<CrmCompany>(`/api/v1/crm/companies/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    // Contacts
    /** List contacts with filters */
    getContacts: (options?: {
      companyId?: string;
      search?: string;
      role?: string;
      hasEmail?: boolean;
      limit?: number;
      offset?: number;
    }) => {
      const params = new URLSearchParams();
      if (options?.companyId) params.set('companyId', options.companyId);
      if (options?.search) params.set('search', options.search);
      if (options?.role) params.set('role', options.role);
      if (options?.hasEmail) params.set('hasEmail', 'true');
      if (options?.limit) params.set('limit', String(options.limit));
      if (options?.offset) params.set('offset', String(options.offset));
      const query = params.toString();
      return fetchApi<CrmContact[]>(`/api/v1/crm/contacts${query ? `?${query}` : ''}`);
    },

    /** Create new contact */
    createContact: (data: CreateCrmContactInput) =>
      fetchApi<CrmContact>('/api/v1/crm/contacts', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    /** Update contact */
    updateContact: (id: string, data: Partial<CreateCrmContactInput>) =>
      fetchApi<CrmContact>(`/api/v1/crm/contacts/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    // Pipelines
    /** List all pipelines with stages */
    getPipelines: () => fetchApi<CrmPipeline[]>('/api/v1/crm/pipelines'),

    /** Create new pipeline */
    createPipeline: (data: { name: string; description?: string; isDefault?: boolean }) =>
      fetchApi<CrmPipeline>('/api/v1/crm/pipelines', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    /** Create stage in pipeline */
    createStage: (data: { pipelineId: string; name: string; position: number; probability?: number; color?: string }) =>
      fetchApi<CrmStage>('/api/v1/crm/stages', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    // Deals
    /** List deals with filters */
    getDeals: (options?: {
      pipelineId?: string;
      stageId?: string;
      companyId?: string;
      ownerId?: string;
      status?: string;
      limit?: number;
      offset?: number;
    }) => {
      const params = new URLSearchParams();
      if (options?.pipelineId) params.set('pipelineId', options.pipelineId);
      if (options?.stageId) params.set('stageId', options.stageId);
      if (options?.companyId) params.set('companyId', options.companyId);
      if (options?.ownerId) params.set('ownerId', options.ownerId);
      if (options?.status) params.set('status', options.status);
      if (options?.limit) params.set('limit', String(options.limit));
      if (options?.offset) params.set('offset', String(options.offset));
      const query = params.toString();
      return fetchApi<CrmDeal[]>(`/api/v1/crm/deals${query ? `?${query}` : ''}`);
    },

    /** Get single deal with details */
    getDeal: (id: string) => fetchApi<CrmDealDetail>(`/api/v1/crm/deals/${id}`),

    /** Create new deal */
    createDeal: (data: CreateCrmDealInput) =>
      fetchApi<CrmDeal>('/api/v1/crm/deals', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    /** Update deal */
    updateDeal: (id: string, data: Partial<CreateCrmDealInput>) =>
      fetchApi<CrmDeal>(`/api/v1/crm/deals/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    // Activities
    /** List activities */
    getActivities: (options?: {
      companyId?: string;
      contactId?: string;
      dealId?: string;
      type?: string;
      limit?: number;
    }) => {
      const params = new URLSearchParams();
      if (options?.companyId) params.set('companyId', options.companyId);
      if (options?.contactId) params.set('contactId', options.contactId);
      if (options?.dealId) params.set('dealId', options.dealId);
      if (options?.type) params.set('type', options.type);
      if (options?.limit) params.set('limit', String(options.limit));
      const query = params.toString();
      return fetchApi<CrmActivity[]>(`/api/v1/crm/activities${query ? `?${query}` : ''}`);
    },

    /** Create activity */
    createActivity: (data: CreateCrmActivityInput) =>
      fetchApi<CrmActivity>('/api/v1/crm/activities', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    // Signals (AI Prospecting Intelligence)
    /** List signals */
    getSignals: (options?: {
      companyId?: string;
      type?: string;
      status?: string;
      minScore?: number;
      limit?: number;
    }) => {
      const params = new URLSearchParams();
      if (options?.companyId) params.set('companyId', options.companyId);
      if (options?.type) params.set('type', options.type);
      if (options?.status) params.set('status', options.status);
      if (options?.minScore) params.set('minScore', String(options.minScore));
      if (options?.limit) params.set('limit', String(options.limit));
      const query = params.toString();
      return fetchApi<CrmSignal[]>(`/api/v1/crm/signals${query ? `?${query}` : ''}`);
    },

    /** Update signal status */
    updateSignal: (id: string, data: { status?: string; notes?: string }) =>
      fetchApi<CrmSignal>(`/api/v1/crm/signals/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    // Building Intelligence
    /** List buildings with network data */
    getBuildings: (options?: {
      minCompanies?: number;
      hasRecentActivity?: boolean;
      limit?: number;
    }) => {
      const params = new URLSearchParams();
      if (options?.minCompanies) params.set('minCompanies', String(options.minCompanies));
      if (options?.hasRecentActivity) params.set('hasRecentActivity', 'true');
      if (options?.limit) params.set('limit', String(options.limit));
      const query = params.toString();
      return fetchApi<CrmBuilding[]>(`/api/v1/crm/buildings${query ? `?${query}` : ''}`);
    },

    /** Create/update building intelligence */
    createBuilding: (data: { address: string; name?: string; type?: string; companyId?: string }) =>
      fetchApi<CrmBuilding>('/api/v1/crm/buildings', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    // Field Definitions (Custom Fields)
    /** Get field definitions for an entity type */
    getFieldDefinitions: (options?: { entityType?: 'company' | 'contact' | 'deal' }) => {
      const params = new URLSearchParams();
      if (options?.entityType) params.set('entityType', options.entityType);
      const query = params.toString();
      return fetchApi<CrmFieldDefinition[]>(`/api/v1/crm/fields${query ? `?${query}` : ''}`);
    },

    /** Create custom field definition */
    createFieldDefinition: (data: CreateCrmFieldDefinitionInput) =>
      fetchApi<CrmFieldDefinition>('/api/v1/crm/fields', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    /** Update custom field definition */
    updateFieldDefinition: (id: string, data: UpdateCrmFieldDefinitionInput) =>
      fetchApi<CrmFieldDefinition>(`/api/v1/crm/fields/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    /** Delete custom field definition */
    deleteFieldDefinition: (id: string) =>
      fetchApi<void>(`/api/v1/crm/fields/${id}`, {
        method: 'DELETE',
      }),

    /** Seed default pipeline and stages */
    seedPipeline: () =>
      fetchApi<{ pipeline: CrmPipeline; stages: CrmStage[] }>('/api/v1/crm/seed', {
        method: 'POST',
      }),
  },

  // Automations
  automations: {
    /** List all automations with last run info */
    list: () => fetchApi<AutomationsResponse>('/api/v1/automations'),

    /** Get single automation with recent runs */
    get: (id: string) =>
      fetchApi<Automation & { recentRuns: AutomationRun[] }>(`/api/v1/automations/${id}`),

    /** Update automation (enable/disable) */
    update: (id: string, data: { enabled: boolean }) =>
      fetchApi<Automation>(`/api/v1/automations/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    /** Manually trigger automation */
    run: (id: string, options?: { testMode?: boolean; testData?: unknown }) =>
      fetchApi<{
        runId: number;
        status: 'success' | 'failed';
        result: { success: boolean; message?: string; data?: unknown };
        duration_ms: number;
      }>(`/api/v1/automations/${id}/run`, {
        method: 'POST',
        body: JSON.stringify(options || { testMode: true }),
      }),

    /** Get execution history for an automation */
    getRuns: (id: string, options?: { limit?: number; offset?: number }) => {
      const params = new URLSearchParams();
      if (options?.limit) params.set('limit', String(options.limit));
      if (options?.offset) params.set('offset', String(options.offset));
      const query = params.toString();
      return fetchApi<AutomationRun[]>(`/api/v1/automations/${id}/runs${query ? `?${query}` : ''}`);
    },

    /** Get recent runs across all automations */
    getRecentRuns: (limit = 20) =>
      fetchApi<AutomationRun[]>(`/api/v1/automations/runs/recent?limit=${limit}`),

    /** Get recipients for an automation */
    getRecipients: (id: string) =>
      fetchApi<AutomationRecipient[]>(`/api/v1/automations/${id}/recipients`),

    /** Add a recipient to an automation */
    addRecipient: (
      id: string,
      data: { recipientType: string; recipientValue: string; isCc?: boolean }
    ) =>
      fetchApi<AutomationRecipient>(`/api/v1/automations/${id}/recipients`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    /** Remove a recipient from an automation */
    removeRecipient: (automationId: string, recipientId: number) =>
      fetchApi<{ success: boolean }>(`/api/v1/automations/${automationId}/recipients/${recipientId}`, {
        method: 'DELETE',
      }),

    /** Update automation configuration (email template, etc.) */
    updateConfig: (
      id: string,
      data: {
        name?: string;
        description?: string;
        enabled?: boolean;
        triggerConfig?: Record<string, unknown>;
        emailSubject?: string;
        emailTemplate?: string;
        config?: Record<string, unknown>;
      }
    ) =>
      fetchApi<Automation>(`/api/v1/automations/${id}/config`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    /** Get dashboard metrics overview */
    getDashboard: () =>
      fetchApi<{
        summary: {
          success_24h: string | number;
          failed_24h: string | number;
          total_24h: string | number;
          avg_duration_7d: string | number | null;
          unique_automations_24h: string | number;
        };
        failureRates: Array<{
          automation_id: string;
          automation_name: string;
          category: string;
          total_runs: string | number;
          failed_runs: string | number;
          failure_rate: string | number;
        }>;
        recentFailures: Array<{
          id: number;
          automationId: string;
          automationName: string;
          startedAt: string;
          error: string | null;
          durationMs: number | null;
        }>;
      }>('/api/v1/automations/dashboard'),

    /** Get currently running automations */
    getLiveRuns: () =>
      fetchApi<Array<{
        id: number;
        automationId: string;
        automationName: string;
        startedAt: string;
        runningFor: number;
        steps?: AutomationStep[];
      }>>('/api/v1/automations/runs/live'),

    /** Get a single run with full details */
    getRunDetail: (automationId: string, runId: number) =>
      fetchApi<AutomationRun>(`/api/v1/automations/${automationId}/runs/${runId}`),

    /** Replay a previous run */
    replay: (
      automationId: string,
      runId: number,
      options?: { testMode?: boolean; testData?: unknown; event?: unknown }
    ) =>
      fetchApi<{ runId: number; result: { success: boolean; error?: string } }>(
        `/api/v1/automations/${automationId}/replay/${runId}`,
        {
          method: 'POST',
          body: JSON.stringify(options || { testMode: true }),
        }
      ),

    /** Search automations (AI-friendly) */
    search: (options?: { q?: string; tags?: string[]; entities?: string[]; enabled?: boolean }) => {
      const params = new URLSearchParams();
      if (options?.q) params.set('q', options.q);
      if (options?.tags) params.set('tags', options.tags.join(','));
      if (options?.entities) params.set('entities', options.entities.join(','));
      if (options?.enabled !== undefined) params.set('enabled', String(options.enabled));
      const query = params.toString();
      return fetchApi<Automation[]>(`/api/v1/automations/search${query ? `?${query}` : ''}`);
    },

    /** Get full automation explanation */
    explain: (id: string) =>
      fetchApi<Automation & {
        howItWorks?: string;
        triggerExplanation?: string;
        exampleScenario?: string;
        variablesUsed?: Record<string, string>;
        recentRunStats?: {
          total: number;
          successRate: number;
          avgDuration: number;
        };
      }>(`/api/v1/automations/${id}/explain`),
  },

  // User Simulator / Impersonation API
  impersonation: {
    /** Get available device presets for simulation */
    getDevicePresets: () =>
      fetchApi<DevicePreset[]>('/api/v1/impersonation/device-presets'),

    /** Get users the current admin can impersonate */
    getEligibleUsers: (options?: {
      branchId?: string;
      search?: string;
      limit?: number;
      offset?: number;
    }) => {
      const params = new URLSearchParams();
      if (options?.branchId) params.set('branchId', options.branchId);
      if (options?.search) params.set('search', options.search);
      if (options?.limit) params.set('limit', String(options.limit));
      if (options?.offset) params.set('offset', String(options.offset));
      const query = params.toString();
      return fetchApi<EligibleUser[]>(`/api/v1/impersonation/eligible-users${query ? `?${query}` : ''}`);
    },

    /** Start an impersonation session */
    startSession: (data: { targetUserId: string; devicePreset?: string }) =>
      fetchApi<ImpersonationStartResponse>('/api/v1/impersonation/start', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    /** Validate an impersonation token */
    validateToken: (token: string) =>
      fetchApi<ImpersonationValidateResponse>('/api/v1/impersonation/validate', {
        method: 'POST',
        body: JSON.stringify({ token }),
      }),

    /** End an impersonation session */
    endSession: (token: string) =>
      fetchApi<ImpersonationEndResponse>('/api/v1/impersonation/end', {
        method: 'POST',
        body: JSON.stringify({ token }),
      }),

    /** Log an action during impersonation (called automatically by api client) */
    logAction: (data: {
      token: string;
      actionType: string;
      method?: string;
      path?: string;
      actionDetails?: Record<string, unknown>;
    }) =>
      fetchApi<{ logged: boolean }>('/api/v1/impersonation/log-action', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    /** Get impersonation sessions for audit */
    getSessions: (options?: {
      adminId?: string;
      targetUserId?: string;
      startDate?: string;
      endDate?: string;
      isActive?: boolean;
      limit?: number;
      offset?: number;
    }) => {
      const params = new URLSearchParams();
      if (options?.adminId) params.set('adminId', options.adminId);
      if (options?.targetUserId) params.set('targetUserId', options.targetUserId);
      if (options?.startDate) params.set('startDate', options.startDate);
      if (options?.endDate) params.set('endDate', options.endDate);
      if (options?.isActive !== undefined) params.set('isActive', String(options.isActive));
      if (options?.limit) params.set('limit', String(options.limit));
      if (options?.offset) params.set('offset', String(options.offset));
      const query = params.toString();
      return fetchApi<ImpersonationSession[]>(`/api/v1/impersonation/sessions${query ? `?${query}` : ''}`);
    },

    /** Get actions for a specific impersonation session */
    getSessionActions: (sessionId: number, options?: { limit?: number; offset?: number }) => {
      const params = new URLSearchParams();
      if (options?.limit) params.set('limit', String(options.limit));
      if (options?.offset) params.set('offset', String(options.offset));
      const query = params.toString();
      return fetchApi<ImpersonationSessionActions>(
        `/api/v1/impersonation/sessions/${sessionId}/actions${query ? `?${query}` : ''}`
      );
    },
  },

  // Error Tracking
  errors: {
    /** List error groups with optional filters */
    listGroups: (options?: ErrorGroupFilters) => {
      const params = new URLSearchParams();
      if (options?.status) params.set('status', options.status);
      if (options?.severity) params.set('severity', options.severity);
      if (options?.priority) params.set('priority', options.priority);
      if (options?.category) params.set('category', options.category);
      if (options?.startDate) params.set('startDate', options.startDate);
      if (options?.endDate) params.set('endDate', options.endDate);
      if (options?.limit) params.set('limit', String(options.limit));
      if (options?.offset) params.set('offset', String(options.offset));
      const query = params.toString();
      return fetchApi<ErrorGroupListResponse>(`/api/v1/errors/groups${query ? `?${query}` : ''}`);
    },

    /** Get a single error group with recent events */
    getGroup: (id: string) => fetchApi<ErrorGroupDetail>(`/api/v1/errors/groups/${id}`),

    /** Update an error group's status/priority */
    updateGroup: (id: string, data: ErrorGroupUpdate) =>
      fetchApi<ErrorGroup>(`/api/v1/errors/groups/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    /** List error events with optional filters */
    listEvents: (options?: ErrorEventFilters) => {
      const params = new URLSearchParams();
      if (options?.fingerprint) params.set('fingerprint', options.fingerprint);
      if (options?.userId) params.set('userId', options.userId);
      if (options?.sessionId) params.set('sessionId', options.sessionId);
      if (options?.severity) params.set('severity', options.severity);
      if (options?.startDate) params.set('startDate', options.startDate);
      if (options?.endDate) params.set('endDate', options.endDate);
      if (options?.limit) params.set('limit', String(options.limit));
      if (options?.offset) params.set('offset', String(options.offset));
      const query = params.toString();
      return fetchApi<ErrorEvent[]>(`/api/v1/errors/events${query ? `?${query}` : ''}`);
    },

    /** Cleanup expired error events */
    cleanup: () => fetchApi<{ deletedEventsCount: number; cleanedAt: string }>('/api/v1/errors/cleanup', { method: 'DELETE' }),
  },

  // Issue Reports
  issues: {
    /** Submit a new issue report */
    submit: (data: IssueSubmission) =>
      fetchApi<{ id: string; message: string }>('/api/v1/issues', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    /** List issue reports with optional filters */
    list: (options?: IssueFilters) => {
      const params = new URLSearchParams();
      if (options?.status) params.set('status', options.status);
      if (options?.severity) params.set('severity', options.severity);
      if (options?.classification) params.set('classification', options.classification);
      if (options?.search) params.set('search', options.search);
      if (options?.startDate) params.set('startDate', options.startDate);
      if (options?.endDate) params.set('endDate', options.endDate);
      if (options?.limit) params.set('limit', String(options.limit));
      if (options?.offset) params.set('offset', String(options.offset));
      const query = params.toString();
      return fetchApi<IssueListResponse>(`/api/v1/issues${query ? `?${query}` : ''}`);
    },

    /** Get a single issue report */
    get: (id: string) => fetchApi<IssueReport>(`/api/v1/issues/${id}`),

    /** Update an issue report */
    update: (id: string, data: IssueUpdate) =>
      fetchApi<IssueReport>(`/api/v1/issues/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    /** Trigger AI analysis for an issue */
    analyze: (id: string) =>
      fetchApi<IssueAnalysisResponse>(`/api/v1/issues/${id}/analyze`, {
        method: 'POST',
      }),
  },

  // Sync Verification
  sync: {
    /** Verify sync by comparing DB row counts to source sheet row counts */
    verify: () => fetchApi<SyncVerificationResult>('/api/v1/sync/verify'),

    /** Reconcile records between DB and sheets - shows records in one source but not the other */
    reconcile: (options?: { table?: string; limit?: number }) => {
      const params = new URLSearchParams();
      if (options?.table) params.set('table', options.table);
      if (options?.limit) params.set('limit', String(options.limit));
      const query = params.toString();
      return fetchApi<SyncReconciliationResult>(`/api/v1/sync/reconcile${query ? `?${query}` : ''}`);
    },
  },

  // Branches & Locations
  branches: {
    /** List all branches */
    list: (options?: { includeInactive?: boolean }) => {
      const params = new URLSearchParams();
      if (options?.includeInactive) params.set('includeInactive', 'true');
      const query = params.toString();
      return fetchApi<Branch[]>(`/api/v1/branches${query ? `?${query}` : ''}`);
    },

    /** Get branch by ID */
    get: (id: string) => fetchApi<Branch>(`/api/v1/branches/${id}`),

    /** Get current user's branch info */
    me: () => fetchApi<{
      assignedBranches: (Branch & { isPrimary: boolean; assignedAt: string | null })[];
      defaultBranch: Branch | null;
      currentBranchId: string | null;
      isAllBranches: boolean;
      isMultiBranchUser: boolean;
      isMultiBranchEnabled: boolean;
    }>('/api/v1/branches/me'),

    // Locations (alternate physical locations per branch)
    locations: {
      /** List locations for a branch */
      list: (branchId: string, status?: 'open' | 'closed' | 'all') => {
        const params = new URLSearchParams();
        if (status) params.set('status', status);
        const query = params.toString();
        return fetchApi<BranchLocation[]>(`/api/v1/branches/${branchId}/locations${query ? `?${query}` : ''}`);
      },

      /** Add a new location to a branch */
      add: (branchId: string, data: {
        name: string;
        address: BranchAddress;
        description?: string;
        status?: 'open' | 'closed';
        openDate?: string;
      }) => fetchApi<BranchLocation>(`/api/v1/branches/${branchId}/locations`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),

      /** Update a location (including close/reopen) */
      update: (branchId: string, locationId: string, data: Partial<{
        name: string;
        address: BranchAddress;
        description: string;
        status: 'open' | 'closed';
        openDate: string;
        closeDate: string;
      }>) => fetchApi<BranchLocation>(`/api/v1/branches/${branchId}/locations/${locationId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

      /** Permanently delete a location (SuperAdmin only) */
      delete: (branchId: string, locationId: string) =>
        fetchApi<null>(`/api/v1/branches/${branchId}/locations/${locationId}`, {
          method: 'DELETE',
        }),

      /** Geocode a location's address (Admin+) */
      geocode: (branchId: string, locationId: string) =>
        fetchApi<{ latitude: number; longitude: number }>(`/api/v1/branches/${branchId}/locations/${locationId}/geocode`, {
          method: 'POST',
        }),
    },

    /** Geocode the primary branch address (Admin+) */
    geocode: (branchId: string) =>
      fetchApi<{ latitude: number; longitude: number }>(`/api/v1/branches/${branchId}/geocode`, {
        method: 'POST',
      }),
  },

  // =========================================================================
  // Timesheets (P14)
  // =========================================================================
  timesheets: {
    // Employee
    my: (params?: { startDate?: string; endDate?: string }) => {
      const qs = new URLSearchParams();
      if (params?.startDate) qs.set('startDate', params.startDate);
      if (params?.endDate) qs.set('endDate', params.endDate);
      const q = qs.toString() ? `?${qs}` : '';
      return fetchApi<any[]>(`/api/v1/timesheets/my${q}`);
    },
    myDate: (date: string) => fetchApi<any>(`/api/v1/timesheets/my/${date}`),
    clockIn: (gps?: any) => fetchApi<any>('/api/v1/timesheets/clock-in', { method: 'POST', body: JSON.stringify({ gps }) }),
    clockOut: (gps?: any) => fetchApi<any>('/api/v1/timesheets/clock-out', { method: 'POST', body: JSON.stringify({ gps }) }),
    breakAction: (data: { action: 'start' | 'end'; type?: string }) =>
      fetchApi<any>('/api/v1/timesheets/break', { method: 'POST', body: JSON.stringify(data) }),
    submit: (date: string) => fetchApi<any>(`/api/v1/timesheets/my/${date}/submit`, { method: 'POST' }),

    // Manager
    team: (params?: { startDate?: string; endDate?: string; status?: string; employeeId?: string }) => {
      const qs = new URLSearchParams();
      if (params?.startDate) qs.set('startDate', params.startDate);
      if (params?.endDate) qs.set('endDate', params.endDate);
      if (params?.status) qs.set('status', params.status);
      if (params?.employeeId) qs.set('employeeId', params.employeeId);
      const q = qs.toString() ? `?${qs}` : '';
      return fetchApi<any[]>(`/api/v1/timesheets/team${q}`);
    },
    notSubmitted: (date?: string) => {
      const qs = new URLSearchParams();
      if (date) qs.set('date', date);
      const q = qs.toString() ? `?${qs}` : '';
      return fetchApi<any[]>(`/api/v1/timesheets/not-submitted${q}`);
    },
    remind: (employeeId: string, date: string) =>
      fetchApi<any>('/api/v1/timesheets/remind', { method: 'POST', body: JSON.stringify({ employeeId, date }) }),
    approve: (id: string) => fetchApi<any>(`/api/v1/timesheets/${id}/approve`, { method: 'POST' }),
    reject: (id: string, notes?: string) =>
      fetchApi<any>(`/api/v1/timesheets/${id}/reject`, { method: 'POST', body: JSON.stringify({ notes }) }),
    bulkApprove: (startDate: string, endDate: string) =>
      fetchApi<{ approvedCount: number; skippedCount: number; totalSubmitted: number }>(
        '/api/v1/timesheets/bulk-approve', { method: 'POST', body: JSON.stringify({ startDate, endDate }) }
      ),

    // Admin
    clockStatus: () => fetchApi<any>('/api/v1/timesheets/clock-status'),
    payPeriods: () => fetchApi<any[]>('/api/v1/timesheets/pay-periods'),
    config: () => fetchApi<any>('/api/v1/timesheets/config'),
    updateConfig: (data: { anchorDate: string; paydayOffset?: number }) =>
      fetchApi<any>('/api/v1/timesheets/config', { method: 'PUT', body: JSON.stringify(data) }),

    // Phase 2B: Manager day detail
    teamDay: (employeeId: string, date: string) =>
      fetchApi<any>(`/api/v1/timesheets/team/${employeeId}/${date}`),
    editClock: (id: string, data: { segmentId: string; field: string; newTime: string }) =>
      fetchApi<any>(`/api/v1/timesheets/${id}/edit-clock`, { method: 'PUT', body: JSON.stringify(data) }),
    annotate: (id: string, data: { text: string; flagForPayroll?: boolean }) =>
      fetchApi<any>(`/api/v1/timesheets/${id}/annotate`, { method: 'POST', body: JSON.stringify(data) }),

    // Phase 2A: Employee edit + waivers
    editMyDay: (date: string, data: { segments?: any[]; breaks?: any[] }) =>
      fetchApi<any>(`/api/v1/timesheets/my/${date}`, { method: 'PUT', body: JSON.stringify(data) }),
    mealBreakResolution: (date: string, data: { mealBreakNumber: number; resolution: string; reason: string }) =>
      fetchApi<any>(`/api/v1/timesheets/my/${date}/meal-break-resolution`, { method: 'POST', body: JSON.stringify(data) }),

    // Phase 2C: Payroll export
    exportPreview: (periodStart: string, periodEnd: string) => {
      const qs = new URLSearchParams({ periodStart, periodEnd });
      return fetchApi<any>(`/api/v1/timesheets/export-preview?${qs}`);
    },
    exportToQbo: (periodStart: string, periodEnd: string) =>
      fetchApi<any>('/api/v1/timesheets/export', { method: 'POST', body: JSON.stringify({ periodStart, periodEnd }) }),
    voidExport: (exportId: string) =>
      fetchApi<any>(`/api/v1/timesheets/export/${exportId}/void`, { method: 'POST' }),
    exportCsv: (exportId: string) =>
      fetchCsvText(`/api/v1/timesheets/export/${exportId}/csv`),
    exportHistory: () => fetchApi<any[]>('/api/v1/timesheets/export-history'),

    // Phase 2E: Holidays
    holidays: (year: number) => fetchApi<any[]>(`/api/v1/timesheets/holidays?year=${year}`),
    addHoliday: (data: { date: string; name: string; paid?: boolean; branchId?: string }) =>
      fetchApi<any>('/api/v1/timesheets/holidays', { method: 'POST', body: JSON.stringify(data) }),
    deleteHoliday: (id: string) =>
      fetchApi<any>(`/api/v1/timesheets/holidays/${id}`, { method: 'DELETE' }),
  },
};

// Email Template types
export interface EmailTemplate {
  id: string;
  name: string;
  description: string | null;
  subjectTemplate: string;
  bodyTemplate: string;
  entityTypes: string | null; // comma-separated: 'client,workorder'
  variables: Record<string, unknown> | null;
  isActive: boolean;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateEmailTemplateInput {
  name: string;
  description?: string;
  subjectTemplate: string;
  bodyTemplate: string;
  entityTypes?: string[];
  variables?: Record<string, unknown>;
  isActive?: boolean;
}

// Catalog types
export interface CatalogStats {
  totalItems: number;
  plantCount: number;
  containerCount: number;
  withPrice: number;
  activeCount: number;
  sizeDefaultCount: number;
  lastUpload: {
    uploadedAt: string;
    uploadedBy: string | null;
    newItems: number;
    updatedItems: number;
  } | null;
}

export interface SizeDefault {
  id: number;
  size: string;
  itemType: string;
  wholesalePrice: string;
  retailPrice: string | null;
  derivedFrom: string | null;
  itemCount: number | null;
  updatedAt: string;
}

export interface CostFormula {
  id: string;
  name: string;
  description: string | null;
  formula: string;
  isActive: boolean;
  updatedAt: string;
}

export interface CatalogUpload {
  id: number;
  filename: string;
  uploadedAt: string;
  uploadedBy: string | null;
  totalRows: number;
  newItems: number;
  updatedItems: number;
  skippedDuplicates: number;
  errors: number;
}

export interface CatalogItem {
  id: number;
  size: string | null;
  plant: string | null;
  workordersPlants: string | null;
  wholesalePrice: number | null;
  floralTag: string | null;
  area: string | null;
  imageUrl: string | null;
  qboItemId: string | null;
  qboDisplayName: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

// QA Report types (Phase 7)
export interface QAScores {
  plantHealth?: number;
  trimming?: number;
  plantCleanliness?: number;
  containerCleanliness?: number;
  staging?: number;
  orchids?: number | null;
  succulents?: number | null;
  tillandsias?: number | null;
  bromeliads?: number | null;
}

export interface QAPhoto {
  url: string;
  annotation?: string;
  rotation?: 0 | 90 | 180 | 270;
}

export interface QAReport {
  id: number;
  firestoreId: string | null;
  submissionDate: string;
  clientId: string;
  clientName: string | null;
  technicianId: string | null;
  technicianName: string | null;
  scores: QAScores | null;
  averageScore: string | null;
  inventoryAccurate: boolean | null;
  salesOpportunity: boolean | null;
  salesOpportunityNotes: string | null;
  reviewerNotes: string | null;
  actionsRequired: string | null;
  photoUrl1: string | null;
  photoUrl2: string | null;
  photoUrl3: string | null;
  photoUrl4: string | null;
  photoUrl5: string | null;
  photoMetadata: {
    labels?: string[];
    migratedFrom?: 'jotform' | 'native';
    migratedAt?: string;
    originalPhotoCount?: number;
    rotations?: Record<number, 0 | 90 | 180 | 270>;
  } | null;
  photos: QAPhoto[] | null;
  createdAt: string;
}

export interface QAStats {
  total: number;
  avgScore: number | null;
  inventoryAccurateCount: number;
  salesOpportunityCount: number;
  byTechnician: Array<{
    technicianId: string | null;
    technicianName: string | null;
    count: number;
    avgScore: number | null;
  }>;
}

export interface QARecommendation {
  clientId: string;
  clientName: string;
  address: string;
  daysSinceLastQA: number;
  lastQADate: string | null;
  lastQAScore: number | null;
  monthlyAmount: number;
  priorityScore: number;
}

export interface QARecommendationsByArea {
  areaCode: string;
  clientCount: number;
  avgPriorityScore: number;
  clients: QARecommendation[];
}

// Route Optimization types
export interface RouteTimeResult {
  routeId: string;
  routeCode: string;
  routeType: string;
  stopCount: number;
  totalTravelMinutes: number;
  totalServiceMinutes: number;
  totalMinutes: number;
  legDurations: number[];
}

export interface RouteOptimizeResult {
  optimizedOrder: string[];
  currentTravelMinutes: number;
  optimizedTravelMinutes: number;
  improvementMinutes: number;
  improvementPercent: number;
}

// Route History types
export interface RouteHistoryEntry {
  id: string;
  name: string;
  description?: string;
  status: 'published' | 'executed' | 'pending' | 'rolled_back';
  effectiveDate: string | null;
  publishedAt: string | null;
  publishedBy: string | null;
  rolledBackAt?: string | null;
  rolledBackBy?: string | null;
  changes: unknown[];
  createdAt: string;
  updatedAt: string;
}

export interface RouteRollbackResult {
  originalId: string;
  rollbackId: string;
  status: string;
}

// Route Draft types
export type RouteDraftStatus = 'draft' | 'pending' | 'published' | 'discarded' | 'rolled_back';

export type RouteDraftChangeType =
  | 'move_stop'
  | 'reorder_stops'
  | 'add_stop'
  | 'remove_stop'
  | 'create_route'
  | 'update_route'
  | 'delete_route';

export interface RouteDraftChange {
  type: RouteDraftChangeType;
  routeId?: string;
  routeCode?: string;
  clientId?: string;
  fromRouteCode?: string;
  toRouteCode?: string;
  newStopOrder?: number;
  previousValue?: unknown;
  newValue?: unknown;
}

export interface RouteDraft {
  id: string;
  name: string;
  description?: string | null;
  status: RouteDraftStatus;
  changes: RouteDraftChange[] | null;
  snapshot?: unknown | null;
  effectiveDate?: string | null;
  publishedAt?: string | null;
  publishedBy?: string | null;
  executedAt?: string | null;
  createdBy: string;
  branchId: string;
  createdAt: string;
  updatedAt: string;
}

export interface RouteDraftPublishResult extends RouteDraft {
  publishId: string;
  changeLogEntries: number;
}

// Missed Service Action types
export interface MissedServiceAction {
  id: number;
  serviceLogId: number;
  clientId: string;
  clientName: string | null;
  missedType: string;
  actionRequired: boolean;
  resolved: boolean;
  notes: string | null;
  createdBy: string | null;
  createdByName: string | null;
  resolvedBy: string | null;
  resolvedByName: string | null;
  resolvedAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface CreateMissedServiceActionInput {
  serviceLogId: number;
  clientId: string;
  clientName?: string | null;
  missedType: string;
  notes?: string | null;
}

export interface UpdateMissedServiceActionInput {
  resolved?: boolean;
  notes?: string;
  actionRequired?: boolean;
}

// Dashboard types
export interface DashboardAlertSummary {
  admin: {
    total: number;
    missingData: number;
    noRoute: number;
    routeNotScheduled: number;
    suspiciousData: number;  // Includes tech mismatch issues
    suspiciousLocations: number;  // Service logs from unusual GPS locations
  };
  clients: {
    total: number;
    aging: number;
    missedService: number;
    partialMissed: number;
    noNextVisit: number;
  };
  workorders: {
    total: number;
    urgent: number;
    stalled: number;
    readyForPickup: number;
  };
  email?: {
    unread: number;
  };
  grandTotal: number;
}

export interface ServiceLog {
  id: number;
  firestoreId: string | null;
  timestamp: string;
  date: string;
  employeeId: string;
  employeeName: string | null;
  clientId: string;
  clientName: string | null;
  timeLog: string | null;
  visitLog: string | null;
  missedService: string | null;
  serviceNotes: string | null;
  latitude: string | null;
  longitude: string | null;
  imageUrl: string | null;
  isLocationSuspicious: boolean | null;
  distanceFromClient: string | null;
  createdAt: string;
  // Edit tracking
  updatedAt?: string | null;
  updatedBy?: string | null;
  updatedByName?: string | null;
  // Missed service detail fields (only in missed service endpoint)
  intervalDays?: number | null;
  nextScheduledDate?: string | null;
  lastCompletedVisit?: string | null;
  daysSinceLastComplete?: number | null;
  daysUntilNextVisit?: number | null;
  gapVsInterval?: number | null;
  // Action tracking fields (only in partial missed endpoint)
  actionId?: number | null;
  actionRequired?: boolean | null;
  actionResolved?: boolean | null;
  actionNotes?: string | null;
}

// Master Schedule Types
export interface MasterScheduleSummary {
  date: string;
  logged: number;
  total: number;
  completed: number;   // Service logs with no missedService flag
  partial: number;     // Service logs with 'Partial Missed'
  missed: number;      // Service logs with missedService (excluding partial)
  unscheduled: number;
  workorders: {
    created: number;
    completed: number;
    open: number;
    urgent: number;
    urgentAdded: number;
  };
}

export interface TechStopWO {
  created: number;
  completed: number;
  open: number;
  urgent: boolean;
  readyForPickup: boolean;
}

export interface TechStop {
  clientId: string;
  clientName: string;
  stopOrder: number;
  stopDuration: number; // Duration in minutes
  status: 'completed' | 'partial' | 'missed' | 'upcoming' | 'current';
  loggedAt: string | null;
  serviceNotes: string | null;
  serviceLogId: number | null;
  workorders: TechStopWO;
  isUnscheduled?: boolean;
}

export interface TechScheduleRow {
  employeeId: string;
  employeeName: string;
  routeCode: string | null;
  schedulerNotes: string | null;
  logged: number;
  total: number;
  unscheduled: number;
  firstLogTime: string | null;
  lastLogTime: string | null;
  stops: TechStop[];
}

// Operations Activity types
export interface OperationsActivityFilters {
  entityType?: string;
  status?: string;
  limit?: number;
  offset?: number;
}

export interface OperationsActivityEntry {
  id: number;
  activityType: 'schedule_draft' | 'schedule_published' | 'route_draft' | 'route_published' | 'route_rollback';
  entityType: 'schedule' | 'route';
  entityId: string | null;
  name: string;
  status: 'draft' | 'pending' | 'executed' | 'failed' | 'rolled_back';
  summary: {
    routesModified?: number;
    clientsAffected?: number;
    [key: string]: unknown;
  } | null;
  effectiveDate: string | null;
  createdBy: string | null;
  createdByName: string | null;
  createdAt: string;
  updatedAt: string;
}

// Unassigned client types
export interface UnassignedClient {
  id: string;
  clientName: string;
  businessName: string | null;
  address: Record<string, unknown> | null;
  areaCode: string | null;
  stopDuration: number | null;
  techId: string | null;
}

// Route Health types
export interface RouteHealthEntry {
  routeId: string;
  routeCode: string;
  employeeName: string | null;
  clientCount: number;
  overdueCount: number;
  healthScore: number;
  status: 'healthy' | 'warning' | 'critical';
}

export interface RouteHealthSummary {
  summary: {
    totalRoutes: number;
    healthyRoutes: number;
    warningRoutes: number;
    criticalRoutes: number;
    totalOverdue: number;
  };
  routes: RouteHealthEntry[];
}

// Audit Log types
export interface AuditLogFilters {
  publishType?: 'route' | 'schedule';
  limit?: number;
  offset?: number;
}

export interface PublishEvent {
  publishId: string;
  publishType: 'route' | 'schedule';
  publishedBy: string;
  publishedByName: string | null;
  publishedAt: string;
  effectiveDate: string | null;
  entryCount: number;
  rolledBack: boolean;
  rolledBackAt: string | null;
}

export interface ChangeLogEntry {
  id: number;
  publishId: string;
  publishType: 'route' | 'schedule';
  publishedBy: string;
  publishedByName: string | null;
  publishedAt: string;
  effectiveDate: string | null;
  entityType: 'client' | 'route' | 'schedule';
  entityId: string;
  field: string;
  fromValue: string | null;
  toValue: string | null;
  description: string;
  rolledBack: boolean;
  rolledBackAt: string | null;
  rolledBackBy: string | null;
  rollbackPublishId: string | null;
  createdAt: string;
}

export interface PublishEventDetail extends PublishEvent {
  entries: ChangeLogEntry[];
  rolledBackBy: string | null;
}

export interface RollbackResult {
  originalPublishId: string;
  rollbackPublishId: string;
  entriesRolledBack: number;
}

// Inventory types (Phase 6)
export interface InventoryContainer {
  id: string;
  clientId: string;
  location: string;
  containerModel?: string | null; // Simplified single field (e.g., "18\" Bronze Ceramic Pot")
  containerType: string | null; // Legacy field
  containerSize: string | null; // Legacy field
  containerMaterial: string | null; // Legacy field
  containerColor: string | null; // Legacy field
  quantity: number;
  notes: string | null;
  primaryPhotoId: string | null;
  status: 'active' | 'removed' | 'pending_verification';
  lastVerified: string | null;
  lastVerifiedBy: string | null;
  estimatedMonthlyValue: string | null;
  createdAt: string;
  updatedAt: string;
  plants?: InventoryPlant[];
  photos?: InventoryPhoto[];
}

export interface InventoryPlant {
  id: string;
  containerId: string;
  plantType: string;
  plantSize: string | null;
  quantity: number;
  position: string | null;
  notes: string | null;
  primaryPhotoId: string | null;
  status: 'active' | 'removed' | 'needs_replacement' | 'ordered';
  healthStatus: 'healthy' | 'fair' | 'poor' | 'dead' | null;
  lastReplaced: string | null;
  wholesaleValue: string | null;
  catalogItemId: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface InventoryPlantWithContext extends InventoryPlant {
  container: {
    id: string;
    location: string;
    containerType: string | null;
    containerSize: string | null;
  } | null;
  client: {
    id: string;
    name: string;
  } | null;
}

export interface InventoryPlantSearchResult extends InventoryPlant {
  containerLocation: string | null;
}

export interface InventoryPhoto {
  id: string;
  entityType: 'container' | 'plant' | 'location';
  entityId: string;
  url: string;
  thumbnailUrl: string | null;
  isPrimary: boolean;
  uploadedBy: string | null;
  uploadedByName: string | null;
  uploadedAt: string;
  notes: string | null;
}

export interface PendingInventoryReview {
  clientId: string;
  clientName: string | null;
  changeCount: number;
  netPricingImpact: number;
  oldestChangeAt: string;
}

export interface InventoryChangeRequest {
  id: number;
  changeType: string;
  clientId: string;
  clientName: string | null;
  containerId: string | null;
  plantId: string | null;
  previousValue: unknown;
  newValue: unknown;
  description: string | null;
  photoIds: string[];
  status: 'pending' | 'approved' | 'rejected' | 'auto_approved';
  submittedBy: string | null;
  submittedByName: string | null;
  submittedAt: string;
  reviewedBy: string | null;
  reviewedByName: string | null;
  reviewedAt: string | null;
  reviewNotes: string | null;
  pricingImpact: string | null;
  timeImpact: number | null;
  effectsApplied: boolean;
  sourceType: string | null;
  sourceId: string | null;
  createdAt: string;
}

export interface InventoryRequest {
  id: number;
  requestType: 'verify' | 'create' | 'update_photos' | 'investigate';
  clientId: string;
  clientName: string | null;
  assignedTo: string | null;
  assignedToName: string | null;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  notes: string | null;
  requirePhotos: boolean;
  dueDate: string | null;
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  completedAt: string | null;
  completedBy: string | null;
  completedByName: string | null;
  completionNotes: string | null;
  changeRequestId: number | null;
  createdBy: string | null;
  createdByName: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface InventoryImport {
  id: number;
  clientId: string | null;
  clientName: string | null;
  filename: string | null;
  containersCreated: number;
  containersUpdated: number;
  plantsCreated: number;
  plantsUpdated: number;
  errorsCount: number;
  errors: Array<{ row?: number; field?: string; message: string }> | null;
  rawData: unknown;
  importedBy: string | null;
  importedByName: string | null;
  importedAt: string;
}

export interface InventoryImportsResponse {
  data: InventoryImport[];
  meta: {
    total: number;
    limit: number;
    offset: number;
  };
  stats: {
    totalContainersCreated: number;
    totalContainersUpdated: number;
    totalPlantsCreated: number;
    totalPlantsUpdated: number;
    totalErrors: number;
  };
}

export interface ClientInventory {
  client: {
    id: string;
    name: string;
    inventoryStatus: string | null;
    inventoryLastUpdated: string | null;
    inventoryDocUrl: string | null;
  };
  containers: InventoryContainer[];
  stats: {
    totalLocations: number;
    totalContainers: number;
    totalPlants: number;
    estimatedMonthlyValue: number;
  };
  recentChanges: InventoryChangeRequest[];
}

// Migration types
export interface MigrationQueueClient {
  id: string;
  clientName: string;
  inventoryDocUrl: string | null;
  inventoryStatus: string | null;
  migrationStatus: 'pending' | 'in_progress' | 'completed' | 'skipped';
  lastUpdated: string | null;
  containerCount: number;
  plantCount: number;
}

export interface MigrationQueue {
  clients: MigrationQueueClient[];
  stats: {
    total: number;
    completed: number;
    pending: number;
    inProgress: number;
  };
}

export interface MigrationClientData {
  client: {
    id: string;
    clientName: string;
    inventoryDocUrl: string | null;
    inventoryStatus: string | null;
    inventoryLastUpdated: string | null;
  };
  containers: InventoryContainer[];
}

export interface MigrationDocContent {
  docId: string;
  title: string;
  content: string;
  contentLength: number;
}

export interface MigrationParsedPlant {
  plantType: string;
  plantSize?: string;
  quantity: number;
  position?: string;
  notes?: string;
  confidence: 'high' | 'medium' | 'low';
}

export interface MigrationParsedContainer {
  location: string;
  containerModel?: string; // Simplified single field (e.g., "18\" Bronze Ceramic Pot")
  containerType?: string; // Legacy field
  containerSize?: string; // Legacy field
  containerMaterial?: string; // Legacy field
  containerColor?: string; // Legacy field
  quantity?: number;
  notes?: string;
  confidence: 'high' | 'medium' | 'low';
  plants: MigrationParsedPlant[];
}

export interface MigrationParseResult {
  containers: MigrationParsedContainer[];
  warnings: string[];
  rawText: string;
}

export interface MigrationSaveResult {
  containersCreated: number;
  plantsCreated: number;
  inventoryStatus: string;
}

export interface MigrationTrainingExample {
  rawDocument: string;
  correctedOutput: MigrationParsedContainer[];
  accuracy: number;
}

export interface MigrationGeneratedPrompt {
  prompt: string;
  examplesUsed: number;
  totalTrainingExamples: number;
}

export interface MigrationBatchResult {
  results: Array<{
    clientId: string;
    clientName: string;
    success: boolean;
    containersCreated?: number;
    plantsCreated?: number;
    error?: string;
  }>;
  summary: {
    total: number;
    successful: number;
    failed: number;
  };
}

export interface MigrationStats {
  totalActiveClients: number;
  clientsWithInventoryDoc: number;
  clientsWithoutInventoryDoc: number;
  migrationStatus: {
    pending: number;
    needsVerification: number;
    complete: number;
  };
  percentWithDocs: number;
  percentMigrated: number;
}

// Alert Dismissal types
export type AlertType = 'missing_geocode' | 'geocode_outlier' | 'invalid_interval' | 'duplicate_name' | 'missing_data';
export type ReasonCode = 'verified_correct' | 'intentionally_blank' | 'client_confirmed' | 'not_applicable' | 'other';
export type DismissType = 'one_time' | 'ongoing';

export interface AlertDismissal {
  id: number;
  alertType: AlertType;
  entityType: 'client';
  entityId: string;
  dataHash: string;
  dismissedBy: string;
  dismissedByName: string | null;
  dismissedAt: string;
  reasonCode: ReasonCode;
  reasonNote: string | null;
  dismissType: DismissType;
  expiresAt?: string;
}

export interface DismissAlertInput {
  alertType: AlertType;
  entityType: 'client';
  entityId: string;
  reasonCode: ReasonCode;
  reasonNote?: string;
  dismissType?: DismissType;
}

export interface GeocodeResult {
  latitude: number;
  longitude: number;
  formattedAddress: string;
}

// User Management types
export interface WorkspaceUser {
  id: number;
  email: string;
  fullName: string | null;
  department: string | null;
  jobTitle: string | null;
  photoUrl: string | null;
  workspaceDomain: string;
  isSuspended: boolean;
  linkedEmployeeId: string | null;
  linkedEmployeeName: string | null;
  hasFirebaseAccount: boolean;
  invitationStatus: 'none' | 'pending' | 'sent' | 'accepted';
}

export interface UserInvitation {
  id: number;
  employeeId: string;
  employeeName: string;
  email: string;
  status: string;
  invitedByName: string | null;
  invitedAt: string;
  expiresAt: string;
  isExpired: boolean;
}

// Daily Report types
export interface ScheduledStop {
  stopOrder: number;
  clientId: string;
  clientName: string;
  address: string;
  estimatedDuration: number;
  hasUrgentWO: boolean;
  loggedAt: string | null;
  logSequence: number | null;
  outOfOrder: boolean;
  serviceNotes: string | null;
  photoCount: number;
  woCreated: number;
  woCompleted: number;
  woOpen: number;
  status: 'completed' | 'missed';
}

export interface UnscheduledVisit {
  clientId: string;
  clientName: string;
  address: string;
  loggedAt: string;
  serviceNotes: string | null;
  photoCount: number;
  woCreated: number;
  woCompleted: number;
  woOpen: number;
}

export interface TechDayReport {
  employeeId: string;
  employeeName: string;
  routeCode: string | null;
  logged: number;
  scheduled: number;
  missed: number;
  unscheduled: number;
  firstLogTime: string | null;
  lastLogTime: string | null;
  workordersCreated: number;
  workordersCompleted: number;
  status: 'complete' | 'issues' | 'activity';
  schedulerNotes: string | null;
  scheduledStops: ScheduledStop[];
  unscheduledVisits: UnscheduledVisit[];
}

export interface DailyReportWorkorderSummary {
  created: number;
  completed: number;
  urgentOutstanding: number;
}

export interface DailyReport {
  date: string;
  generatedAt: string;
  summary: {
    techCount: number;
    totalScheduled: number;
    totalLogged: number;
    totalMissed: number;
    totalUnscheduled: number;
    workorders: DailyReportWorkorderSummary;
  };
  techs: TechDayReport[];
}

export interface DailyReportSummary {
  id: string;
  date: string;
  generatedAt: string;
  techCount: number;
  totalScheduled: number;
  totalLogged: number;
  totalMissed: number;
  totalUnscheduled: number;
  woCreated: number;
  woCompleted: number;
}

// Gmail Integration Types
export interface GmailConnectionStatus {
  connected: boolean;
  email: string | null;
  connectedAt: string | null;
}

export interface SendEmailInput {
  to: string;
  cc?: string[];
  subject: string;
  body: string;
  entityType?: string;
  entityId?: string;
  /** Gmail Message-ID of the email being replied to (for threading) */
  inReplyTo?: string;
  /** Gmail Thread ID to group the reply into the same thread */
  threadId?: string;
  /** Message-ID chain for threading (RFC 2822) */
  references?: string[];
}

export interface SendEmailResult {
  emailId: string;
  gmailMessageId: string | null;
  gmailThreadId: string | null;
  sentAt: string;
}

// Invoice-Aware Collection Email Types
export interface SendCollectionEmailInput {
  qboCustomerId: string;
  toEmail: string;
  ccEmails?: string[];
  subject: string;
  body: string;
  templateId?: string;
  invoicesCovered: string[]; // QBO invoice IDs (required)
  attachInvoicePdfs?: boolean; // Default: true
}

export interface SendCollectionEmailResult {
  success: boolean;
  invoiceEmailId: string;
  collectionTouchId: string;
  gmailMessageId: string | null;
  sentVia: 'oauth' | 'service_account';
  fromEmail: string;
  attachmentCount: number;
  invoicesCovered: string[];
}

export interface InvoiceEmail {
  id: string;
  qboCustomerId: string;
  toEmail: string;
  ccEmails: string[] | null;
  subject: string;
  bodyHtml: string;
  templateId: string | null;
  invoicesCovered: string[];
  invoiceTableHtml: string | null;
  hasAttachments: boolean;
  attachmentCount: number;
  collectionTouchId: string | null;
  status: string;
  sentVia: string;
  fromEmail: string;
  replyTo: string;
  gmailMessageId: string | null;
  sentAt: string;
  sentBy: string;
  branchId: string | null;
  createdAt: string;
}

export interface ReceivedEmail {
  id: string;
  gmailMessageId: string;
  gmailThreadId: string | null;
  fromEmail: string;
  toEmail: string;
  subject: string | null;
  bodyPreview: string | null;
  bodyHtml: string | null;
  receivedAt: string;
  trackingCode: string | null;
  linkedEntityType: string | null;
  linkedEntityId: string | null;
  linkedInvoiceEmailId: string | null;
  linkedQboCustomerId: string | null;
  collectionTouchId: string | null;
  status: string;
  processedAt: string | null;
  userId: string;
  createdAt: string;
  // Email management fields (used by useEmailManagement hooks)
  readAt: string | null;
  isStarred: boolean;
  archivedAt: string | null;
  snoozedUntil: string | null;
}

export interface EmailThreadItem {
  id: string;
  type: 'sent' | 'received';
  date: string | null;
  subject: string | null;
  toEmail: string;
  fromEmail: string;
  bodyPreview: string;
  // Sent email specific
  invoicesCovered?: string[];
  hasAttachments?: boolean;
  attachmentCount?: number;
  gmailMessageId?: string | null;
  // Received email specific
  matched?: boolean;
  linkedInvoiceEmailId?: string | null;
  touchId?: string | null;
}

export interface EmailThreadResponse {
  thread: EmailThreadItem[];
  sentCount: number;
  receivedCount: number;
}

export interface SentEmail {
  id: string;
  senderEmail: string;
  senderName?: string;
  recipientEmail: string;
  ccEmails: string | null;
  subject: string;
  bodyPreview: string | null;
  bodyHtml?: string | null;
  entityType: string | null;
  entityId: string | null;
  sentAt: string;
  gmailMessageId?: string | null;
  gmailThreadId?: string | null;
  senderUserId?: string;
  // Email management fields (match database schema)
  isStarred: boolean;
  archivedAt: string | null;
}

// Email inbox response - unified inbox with received and sent emails
export interface EmailInboxResponse {
  received: ReceivedEmail[];
  sent: SentEmail[];
  total: number;
}

// Scheduled email for delayed send feature
export interface ScheduledEmail {
  id: string;
  userId: string;
  toEmail: string;
  subject: string;
  body: string;
  sendAt: string;
  status: 'scheduled' | 'sent' | 'cancelled' | 'failed';
  entityType?: string;
  entityId?: string;
  createdAt: string;
  updatedAt: string;
}

// Email follow-up reminder
export interface EmailFollowUp {
  id: string;
  emailId: string;
  userId: string;
  followUpAt: string;
  notes?: string;
  status: 'pending' | 'completed' | 'dismissed';
  createdAt: string;
  updatedAt: string;
  email?: {
    id: string;
    subject: string;
    fromEmail?: string;
    toEmail?: string;
  };
}

// Comment on an email
export interface EmailComment {
  id: string;
  emailId: string;
  userId: string;
  content: string;
  createdAt: string;
}

// QuickBooks Online Integration Types
export interface QboConnection {
  id: string;
  realmId: string;
  companyName: string;
  branchId: string | null;
  branchName?: string | null;
  environment: string;
  syncEnabled: boolean;
  lastSyncAt: string | null;
  lastSyncStatus: string | null;
  lastSyncError: string | null;
  tokenExpiresAt: string;
  refreshTokenExpiresAt: string | null;
  connectedBy: string;
  connectedByName: string | null;
  connectedAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface QboCustomer {
  id: string;
  qboId: string;
  connectionId: string;
  displayName: string;
  companyName: string | null;
  givenName: string | null;
  familyName: string | null;
  primaryEmailAddress: string | null;
  primaryPhone: string | null;
  billAddr: Record<string, unknown> | null;
  shipAddr: Record<string, unknown> | null;
  balance: string | null;
  active: boolean;
  lastSyncAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface QboInvoice {
  id: string;
  qboId: string;
  docNumber: string | null;
  customerDisplayName: string | null;
  txnDate: string | null;
  dueDate: string | null;
  totalAmt: string | null;
  balance: string | null;
  status: string | null;
  emailStatus: string | null;
  lastSyncAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface QboPayment {
  id: string;
  qboId: string;
  txnDate: string | null;
  totalAmt: string | null;
  customerDisplayName: string | null;
  paymentMethodRef: string | null;
  depositToAccountRef: string | null;
  lastSyncAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface QboSyncLogEntry {
  id: number;
  connectionId: string;
  syncType: string;
  status: string;
  startedAt: string;
  completedAt: string | null;
  recordsProcessed: number | null;
  errors: string[] | null;
}

// =============================================================================
// Finance Module Types
// =============================================================================

export interface FinanceSettings {
  autoCreateCollectionDays: number;
  defaultCollectionPriority: string;
  autoAssignCollections: boolean;
  warningThresholdDays: number;
  criticalThresholdDays: number;
  writeOffThresholdDays: number;
  enableCollectionAlerts: boolean;
  enableFollowupReminders: boolean;
  followupReminderDays: number;
  enableAgingDigest: boolean;
  agingDigestDay: string;
  minAmountForCollection: number;
  excludePaymentPlanCustomers: boolean;
}

export interface FinanceDashboardData {
  aging: {
    bucket0To30: string;
    bucket31To60: string;
    bucket61To90: string;
    bucket91Plus: string;
    totalOutstanding: string;
    count0To30: number;
    count31To60: number;
    count61To90: number;
    count91Plus: number;
    totalInvoices: number;
  };
  collections: {
    activeCount: number;
    customersOverdue: number;
  };
  activity: {
    upcomingFollowups: number;
    recentOutreachCount: number;
  };
}

export interface FinanceCollection {
  id: number;
  clientId: string | null;
  qboCustomerId: string | null;
  invoiceId: number | null;
  status: string;
  priority: string | null;
  daysOverdue: number;
  totalOwed: string;
  oldestInvoiceDate: string | null;
  assignedTo: string | null;
  lastContactDate: string | null;
  lastContactMethod: string | null;
  lastContactOutcome: string | null;
  nextFollowupDate: string | null;
  notes: string | null;
  paymentPlanAmount: string | null;
  paymentPlanFrequency: string | null;
  paymentPlanStartDate: string | null;
  resolvedAt: string | null;
  resolvedBy: string | null;
  resolutionNotes: string | null;
  branchId: string | null;
  createdAt: string;
  updatedAt: string;
  // Enriched fields from list endpoint
  customerName?: string | null;
  customerEmail?: string | null;
  customerPhone?: string | null;
  recentOutreach?: {
    outcome: string | null;
    promisedDate: string | null;
    createdAt: string;
  }[];
  isFirstOverdue?: boolean;
}

export interface FinanceCollectionDetail extends FinanceCollection {
  customer: QboCustomer | null;
  invoices: QboInvoice[];
  outreach: FinanceOutreach[];
  assignedEmployee: { id: string; name: string } | null;
}

export interface FinanceOutreach {
  id: number;
  collectionId: number;
  outreachType: string;
  outcome: string | null;
  contactName: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
  notes: string | null;
  promisedAmount: string | null;
  promisedDate: string | null;
  followupRequired: boolean;
  followupDate: string | null;
  followupNotes: string | null;
  sentEmailId: number | null;
  createdBy: string;
  branchId: string | null;
  createdAt: string;
  // Enriched fields
  createdByName?: string;
}

export interface CreateCollectionInput {
  clientId?: string;
  qboCustomerId?: string;
  invoiceId?: number;
  status?: string;
  priority?: string;
  assignedTo?: string;
  notes?: string;
  nextFollowupDate?: string;
  branchId?: string;
}

export interface UpdateCollectionInput {
  status?: string;
  priority?: string;
  assignedTo?: string;
  notes?: string;
  nextFollowupDate?: string;
  lastContactDate?: string;
  lastContactMethod?: string;
  lastContactOutcome?: string;
  paymentPlanAmount?: string;
  paymentPlanFrequency?: string;
  paymentPlanStartDate?: string;
}

export interface CreateOutreachInput {
  outreachType: string;
  outcome?: string;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  notes?: string;
  promisedAmount?: string;
  promisedDate?: string;
  followupRequired?: boolean;
  followupDate?: string;
  followupNotes?: string;
}

export interface FinanceAgingReport {
  asOf: string;
  totals: {
    bucket0To30: string;
    bucket31To60: string;
    bucket61To90: string;
    bucket91Plus: string;
    total: string;
    invoiceCount: number;
    clientCount: number;
  };
  clients: FinanceAgingClient[];
}

export interface FinanceAgingClient {
  qboCustomerId: string;
  customerName: string;
  bucket0To30: string;
  bucket31To60: string;
  bucket61To90: string;
  bucket91Plus: string;
  total: string;
  invoiceCount: number;
}

export interface FinanceAgingSnapshot {
  id: number;
  snapshotDate: string;
  bucket0To30: string;
  bucket31To60: string;
  bucket61To90: string;
  bucket91Plus: string;
  totalOutstanding: string;
  count0To30: number;
  count31To60: number;
  count61To90: number;
  count91Plus: number;
  totalInvoices: number;
  clientsWithBalance: number;
  clientsOverdue: number;
  clientDetails: Record<string, unknown>[] | null;
  branchId: string | null;
  createdAt: string;
}

export interface CollectionTabCounts {
  to_bill: number;
  to_send: number;
  to_follow_up: number;
  on_track: number;
  recently_paid: number;
}

// Analytics Types
export interface FinanceAnalytics {
  contactMetrics: {
    totalCollections: number;
    totalAttempts: number;
    avgAttemptsPerCollection: number;
  };
  successRates: Array<{
    status: string;
    total: number;
    resolved: number;
    successRate: number;
  }>;
  promiseToPay: {
    promisesMade: number;
    promisesDue: number;
    promisesKept: number;
    keepRate: number;
  };
  agingTrend: Array<{
    date: string;
    buckets: Array<{
      name: string;
      count: number;
      amount: number;
    }>;
  }>;
  workload: Array<{
    assignedTo: string | null;
    assigneeName: string;
    activeCount: number;
    totalOwed: number;
    highPriorityCount: number;
    avgDaysOverdue: number;
  }>;
}

export interface FinanceAnalyticsParams {
  from: string; // ISO date
  to: string; // ISO date
  branchId?: string;
}

// Recently Paid Data Types
export interface RecentlyPaidInvoice {
  invoiceId: number;
  qboInvoiceId: string;
  docNumber: string | null;
  invoiceDate: string | null;
  dueDate: string | null;
  paidDate: string | null;
  amount: string;
  paid: string;
  daysLate: number;
  balance: string;
  paymentMethod: string | null;
  paymentRefNum: string | null;
}

export interface RecentlyPaidCustomer {
  qboCustomerId: string;
  customerName: string;
  invoices: RecentlyPaidInvoice[];
  totalCollected: string;
  paymentCount: number;
  invoiceCount: number;
  avgDaysLate: number;
}

export interface RecentlyPaidData {
  days: number;
  totals: {
    customerCount: number;
    invoiceCount: number;
    totalCollected: string;
    avgDaysLate: number;
  };
  customers: RecentlyPaidCustomer[];
}

// Unbilled Workorders (To Bill) Types
export interface UnbilledWorkorder {
  id: number;
  firestoreId: string | null;
  itemName: string | null;
  qty: number | null;
  notes: string | null;
  billingNotes: string | null;
  completedAt: string | null;
  timestamp: string;
  assignedToName: string | null;
  estimatedValue: string;
}

export interface UnbilledWorkordersClient {
  clientId: string;
  clientName: string;
  workorders: UnbilledWorkorder[];
  totalEstimatedValue: string;
  workorderCount: number;
  oldestCompletionDate: string | null;
  daysSinceOldest: number;
}

export interface UnbilledWorkordersData {
  totals: {
    clientCount: number;
    workorderCount: number;
    totalEstimatedValue: string;
  };
  clients: UnbilledWorkordersClient[];
}

// Unsent Invoices Types (To Send tab)
export interface UnsentInvoice {
  id: number;
  qboId: string;
  docNumber: string | null;
  invoiceDate: string | null;
  dueDate: string | null;
  totalAmt: string;
  balance: string | null;
  emailStatus: string | null;
  privateNote: string | null;
}

export interface UnsentInvoicesCustomer {
  qboCustomerId: string;
  customerName: string;
  customerEmail: string | null;
  hasEmail: boolean;
  invoices: UnsentInvoice[];
  totalAmount: string;
  invoiceCount: number;
  oldestInvoiceDate: string | null;
  daysSinceOldest: number;
}

export interface UnsentInvoicesData {
  totals: {
    customerCount: number;
    invoiceCount: number;
    totalAmount: string;
    customersWithEmail: number;
    customersWithoutEmail: number;
  };
  customers: UnsentInvoicesCustomer[];
}

// Collection Touches Types (Issue #3)
export interface CollectionTouch {
  id: string;
  qboCustomerId: string;
  type: 'phone_call' | 'email_sent' | 'email_received' | 'note';
  contactSpokenTo: string | null;
  durationMinutes: number | null;
  outcome: string | null;
  notes: string | null;
  nextFollowUpDate: string | null;
  followUpNotes: string | null;
  invoicesCovered: string[] | null;
  createdAt: string;
  createdBy: string;
  branchId: string | null;
  // Enriched fields
  createdByName?: string;
}

export interface CreateCollectionTouchInput {
  qboCustomerId: string;
  type: 'phone_call' | 'email_sent' | 'email_received' | 'note';
  contactSpokenTo?: string;
  durationMinutes?: number;
  outcome?: string;
  notes?: string;
  nextFollowUpDate?: string;
  followUpNotes?: string;
  invoicesCovered?: string[];
}

export interface CustomerCollectionsConfig {
  qboCustomerId: string;
  internalNotes: string | null;
  invoicingInstructions: string | null;
  deliveryMethod: string | null;
  preferredContactMethod: string | null;
  bestTimeToCall: string | null;
  collectionPriority: string | null;
  isOnPaymentPlan: boolean;
  paymentPlanNotes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateCustomerConfigInput {
  internalNotes?: string;
  invoicingInstructions?: string;
  deliveryMethod?: string;
  preferredContactMethod?: string;
  bestTimeToCall?: string;
  collectionPriority?: string;
  isOnPaymentPlan?: boolean;
  paymentPlanNotes?: string;
}

export interface CustomerProfile {
  qboCustomerId: string;
  displayName: string;
  email: string | null;
  phone: string | null;
  balance: string;
  // Related entities
  config: CustomerCollectionsConfig | null;
  invoices: QboInvoice[];
  payments: QboPayment[];
  touches: CollectionTouch[];
  // Aggregations
  aging: {
    current: string;
    bucket1To30: string;
    bucket31To60: string;
    bucket61To90: string;
    bucket91Plus: string;
    total: string;
  };
  stats: {
    totalInvoices: number;
    openInvoices: number;
    totalPayments: number;
    avgDaysToPayment: number;
    lastPaymentDate: string | null;
    lastTouchDate: string | null;
  };
}

// =============================================================================
// CRM Types
// =============================================================================

export interface CrmCompany {
  id: string;
  name: string;
  website: string | null;
  linkedinUrl: string | null;
  address1: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  lat: string | null;
  lng: string | null;
  buildingName: string | null;
  floor: string | null;
  sqFootage: number | null;
  leaseExpiration: string | null;
  industry: string | null;
  employeeCount: number | null;
  annualRevenue: number | null;
  companyType: string | null;
  tier: string | null;
  phone: string | null;
  source: string | null;
  sourceDetails: string | null;
  referredBy: string | null;
  nearbyClientIds: string[] | null;
  buildingClientIds: string[] | null;
  buildingSaturation: string | null;
  status: string;
  temperature: string | null;
  intentScore: number;
  engagementScore: number;
  proximityScore: number;
  totalScore: number | null;
  ownerId: string | null;
  ownerName: string | null;
  convertedToClientId: string | null;
  convertedAt: string | null;
  branchId: string | null;
  notes: string | null;
  lastActivityAt: string | null;
  nextFollowupAt: string | null;
  contactCount: number;
  dealCount: number;
  customFields: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface CrmContact {
  id: string;
  companyId: string | null;
  companyName: string | null;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string | null;
  phone: string | null;
  mobile: string | null;
  linkedinUrl: string | null;
  title: string | null;
  department: string | null;
  role: string | null;
  previousCompanyId: string | null;
  previousCompanyName: string | null;
  jobChangedAt: string | null;
  trackForJobChanges: boolean;
  isChampion: boolean;
  isPrimary: boolean;
  relationshipScore: number;
  preferredContactMethod: string | null;
  timezone: string | null;
  bestTimeToContact: string | null;
  emailOptedOut: boolean;
  emailOptedOutAt: string | null;
  status: string;
  ownerId: string | null;
  ownerName: string | null;
  branchId: string | null;
  notes: string | null;
  lastContactedAt: string | null;
  customFields: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface CrmPipeline {
  id: string;
  name: string;
  description: string | null;
  pipelineType: string;
  isDefault: boolean;
  isActive: boolean;
  branchId: string | null;
  sortOrder: number;
  stages?: CrmStage[];
  createdAt: string;
  updatedAt: string;
}

export interface CrmStage {
  id: string;
  pipelineId: string;
  name: string;
  description: string | null;
  stageType: string;
  probability: number | null;
  color: string | null;
  sortOrder: number;
  position: number;
  autoActions: Record<string, unknown> | null;
  rottingEnabled: boolean;
  rottingDays: number | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CrmDeal {
  id: string;
  name: string;
  companyId: string | null;
  companyName: string | null;
  primaryContactId: string | null;
  primaryContactName: string | null;
  pipelineId: string;
  stageId: string;
  stageName: string | null;
  value: number | null;
  recurringValue: number | null;
  currency: string;
  probability: number;
  weightedValue: number | null;
  expectedCloseDate: string | null;
  actualCloseDate: string | null;
  status: string;
  lostReason: string | null;
  lostReasonDetails: string | null;
  wonReason: string | null;
  competitorId: string | null;
  source: string | null;
  sourceDetails: string | null;
  leadId: string | null;
  ownerId: string | null;
  ownerName: string | null;
  serviceType: string | null;
  proposedPlantCount: number | null;
  proposedVisitFrequency: string | null;
  convertedToClientId: string | null;
  convertedAt: string | null;
  branchId: string | null;
  notes: string | null;
  lastActivityAt: string | null;
  nextFollowupAt: string | null;
  daysSinceLastActivity: number | null;
  daysInCurrentStage: number | null;
  stageEnteredAt: string | null;
  customFields: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface CrmActivity {
  id: string;
  companyId: string | null;
  companyName: string | null;
  contactId: string | null;
  dealId: string | null;
  activityType: string;
  subject: string;
  notes: string | null;
  description: string | null;
  emailDirection: string | null;
  emailOpened: boolean;
  emailOpenedAt: string | null;
  emailClicked: boolean;
  emailClickedAt: string | null;
  emailReplied: boolean;
  emailRepliedAt: string | null;
  emailBounced: boolean;
  callDirection: string | null;
  callDuration: number | null;
  callOutcome: string | null;
  meetingType: string | null;
  meetingDuration: number | null;
  meetingLocation: string | null;
  taskDueDate: string | null;
  taskCompletedAt: string | null;
  taskPriority: string | null;
  sequenceId: string | null;
  touchpointNumber: number | null;
  outcome: string | null;
  nextSteps: string | null;
  followupRequired: boolean;
  followupDate: string | null;
  performedBy: string | null;
  performedByName: string | null;
  createdBy: string | null;
  createdByName: string | null;
  branchId: string | null;
  occurredAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface CrmSignal {
  id: string;
  companyId: string | null;
  companyName: string | null;
  contactId: string | null;
  signalType: string;
  title: string;
  description: string | null;
  sourceUrl: string | null;
  source: string | null;
  strength: string;
  score: number;
  relevanceScore: number;
  confidenceScore: number;
  signalData: Record<string, unknown> | null;
  sourceData: Record<string, unknown> | null;
  status: string;
  reviewedBy: string | null;
  reviewedAt: string | null;
  actionTaken: string | null;
  convertedToCompanyId: string | null;
  convertedToDealId: string | null;
  expiresAt: string | null;
  branchId: string | null;
  detectedAt: string;
  createdAt: string;
}

export interface CrmBuilding {
  id: string;
  name: string;
  address: string;
  address1: string;
  address2: string | null;
  city: string;
  state: string;
  zip: string | null;
  lat: string | null;
  lng: string | null;
  buildingClass: string | null;
  totalFloors: number | null;
  totalSqFt: number | null;
  yearBuilt: number | null;
  propertyType: string | null;
  type: string | null;
  propertyManager: string | null;
  propertyManagerContact: string | null;
  propertyManagerEmail: string | null;
  propertyManagerPhone: string | null;
  ownerCompany: string | null;
  hasExistingClients: boolean;
  existingClientCount: number;
  companyCount: number;
  existingClientIds: string[] | null;
  saturationPercent: string | null;
  lobbyServiced: boolean;
  tenantCount: number | null;
  tenantList: Record<string, unknown>[] | null;
  vacancyRate: string | null;
  prospectPriority: string | null;
  opportunityScore: number;
  dataSource: string | null;
  lastDataUpdate: string | null;
  lastActivity: string | null;
  branchId: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CrmDashboardData {
  companyStats: {
    total: number;
    byStatus: {
      lead: number;
      prospect: number;
      customer: number;
      churned: number;
      inactive: number;
    };
  };
  dealStats: {
    total: number;
    open: number;
    won: number;
    lost: number;
    totalValue: number;
    weightedValue: number;
    wonValue: number;
  };
  contactCount: number;
  signalStats: {
    newCount: number;
    total: number;
  };
  recentActivities: CrmActivity[];
  pipelineSummary: Array<{
    stageId: string;
    stageName: string;
    color: string | null;
    dealCount: number;
    totalValue: number;
  }>;
}

export interface CrmCompanyDetail extends CrmCompany {
  contacts: CrmContact[];
  deals: CrmDeal[];
  activities: CrmActivity[];
  signals: CrmSignal[];
}

export interface CrmDealDetail extends CrmDeal {
  company: CrmCompany | null;
  primaryContact: CrmContact | null;
  activities: CrmActivity[];
}

export interface CreateCrmCompanyInput {
  name: string;
  website?: string;
  linkedinUrl?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
  industry?: string;
  employeeCount?: number;
  companyType?: string;
  tier?: string;
  phone?: string;
  source?: string;
  sourceDetails?: string;
  referredBy?: string;
  status?: string;
  temperature?: string;
  notes?: string;
  branchId?: string;
}

export interface CreateCrmContactInput {
  companyId?: string;
  companyName?: string;
  firstName: string;
  lastName: string;
  fullName?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  linkedinUrl?: string;
  title?: string;
  department?: string;
  role?: string;
  isChampion?: boolean;
  isPrimary?: boolean;
  preferredContactMethod?: string;
  notes?: string;
  branchId?: string;
}

export interface CreateCrmDealInput {
  name: string;
  companyId?: string;
  companyName?: string;
  primaryContactId?: string;
  primaryContactName?: string;
  pipelineId?: string;
  stageId?: string;
  value?: number;
  recurringValue?: number;
  probability?: number;
  expectedCloseDate?: string;
  source?: string;
  sourceDetails?: string;
  serviceType?: string;
  proposedPlantCount?: number;
  proposedVisitFrequency?: string;
  notes?: string;
  branchId?: string;
}

export interface CreateCrmActivityInput {
  companyId?: string;
  contactId?: string;
  dealId?: string;
  activityType: string;
  subject: string;
  notes?: string;
  description?: string;
  emailDirection?: string;
  callDirection?: string;
  callDuration?: number;
  callOutcome?: string;
  meetingType?: string;
  meetingDuration?: number;
  meetingLocation?: string;
  taskDueDate?: string;
  taskPriority?: string;
  outcome?: string;
  nextSteps?: string;
  followupRequired?: boolean;
  followupDate?: string;
  occurredAt?: string;
  branchId?: string;
}

export interface CrmFieldDefinition {
  id: string;
  entityType: 'company' | 'contact' | 'deal';
  fieldKey: string;
  fieldLabel: string;
  fieldType: 'text' | 'number' | 'date' | 'select' | 'multiselect' | 'boolean' | 'url' | 'address' | 'monetary';
  options?: Array<{ value: string; label: string }>;
  isRequired: boolean;
  isVisible: boolean;
  isSearchable: boolean;
  sortOrder: number;
  pipedriveKey: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCrmFieldDefinitionInput {
  entityType: 'company' | 'contact' | 'deal';
  fieldKey: string;
  fieldLabel: string;
  fieldType: 'text' | 'number' | 'date' | 'select' | 'multiselect' | 'boolean' | 'url' | 'address' | 'monetary';
  options?: Array<{ value: string; label: string }>;
  isRequired?: boolean;
  isVisible?: boolean;
  isSearchable?: boolean;
  sortOrder?: number;
  pipedriveKey?: string;
}

export interface UpdateCrmFieldDefinitionInput {
  fieldLabel?: string;
  fieldType?: 'text' | 'number' | 'date' | 'select' | 'multiselect' | 'boolean' | 'url' | 'address' | 'monetary';
  options?: Array<{ value: string; label: string }>;
  isRequired?: boolean;
  isVisible?: boolean;
  isSearchable?: boolean;
  sortOrder?: number;
  pipedriveKey?: string;
}

// Impersonation / User Simulator types
export interface DevicePreset {
  id: string;
  name: string;
  width: number;
  height: number;
}

export interface EligibleUser {
  id: string;
  name: string;
  email: string;
  role: string;
  branchId: string | null;
  profilePhotoUrl: string | null;
}

export interface ImpersonationTargetUser {
  id: string;
  name: string;
  email: string;
  role: string;
  branchId: string | null;
  profilePhotoUrl: string | null;
  isSuperAdmin?: boolean;
}

export interface ImpersonationStartResponse {
  token: string;
  sessionId: number;
  expiresAt: string;
  targetUser: ImpersonationTargetUser;
  device: {
    name: string;
    width: number;
    height: number;
  };
}

export interface ImpersonationValidateResponse {
  valid: boolean;
  reason?: string;
  sessionId?: number;
  adminId?: string;
  adminName?: string;
  expiresAt?: string;
  targetUser?: ImpersonationTargetUser;
}

export interface ImpersonationEndResponse {
  ended: boolean;
  sessionId: number;
  duration: number | null;
  actionCount: number;
}

export interface ImpersonationSession {
  id: number;
  adminId: string;
  adminName: string | null;
  adminEmail: string | null;
  targetUserId: string;
  targetUserName: string | null;
  targetUserEmail: string | null;
  targetUserRole: string | null;
  branchId: string;
  devicePreset: string | null;
  startedAt: string;
  endedAt: string | null;
  isActive: boolean;
  actionCount: number;
  duration: number | null;
}

export interface ImpersonationAction {
  id: number;
  actionType: string;
  method: string | null;
  path: string | null;
  actionDetails: Record<string, unknown> | null;
  performedAt: string;
}

export interface ImpersonationSessionActions {
  session: {
    id: number;
    adminName: string | null;
    targetUserName: string | null;
    startedAt: string;
    endedAt: string | null;
  };
  actions: ImpersonationAction[];
}

// Error Tracking types
export interface ErrorGroup {
  id: string;
  message: string;
  name: string | null;
  stack: string | null;
  severity: 'critical' | 'error' | 'warning' | 'info';
  category: string;
  capturePoint: string | null;
  occurrenceCount: number;
  firstSeenAt: string;
  lastSeenAt: string;
  status: 'new' | 'acknowledged' | 'investigating' | 'resolved' | 'closed';
  priority: 'critical' | 'high' | 'medium' | 'low';
  assignedTo: string | null;
  resolution: string | null;
  lastUserId: string | null;
  lastUserEmail: string | null;
  lastRoute: string | null;
  lastBreadcrumbs: Record<string, unknown> | null;
  sampleErrorId: string | null;
  branchId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ErrorEvent {
  id: string;
  eventId: string;
  fingerprint: string;
  message: string;
  name: string | null;
  stack: string | null;
  severity: 'critical' | 'error' | 'warning' | 'info';
  category: string;
  source: 'client' | 'server';
  capturePoint: string | null;
  userId: string | null;
  userEmail: string | null;
  sessionId: string;
  currentRoute: string | null;
  breadcrumbs: Record<string, unknown> | null;
  browser: string | null;
  deviceType: string | null;
  appVersion: string | null;
  userAgent: string | null;
  timestamp: string;
  expiresAt: string;
  branchId: string | null;
  createdAt: string;
}

export interface ErrorGroupFilters {
  status?: string;
  severity?: string;
  priority?: string;
  category?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
}

export interface ErrorEventFilters {
  fingerprint?: string;
  userId?: string;
  sessionId?: string;
  severity?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
}

export interface ErrorGroupUpdate {
  status?: 'new' | 'acknowledged' | 'investigating' | 'resolved' | 'closed';
  priority?: 'critical' | 'high' | 'medium' | 'low';
  assignedTo?: string | null;
  resolution?: string | null;
}

export interface ErrorGroupStats {
  total: number;
  new: number;
  critical: number;
  errors: number;
  totalOccurrences: number;
}

export interface ErrorGroupListResponse {
  data: ErrorGroup[];
  meta: {
    total: number;
    limit: number;
    offset: number;
  };
  stats: ErrorGroupStats;
}

export interface ErrorGroupDetail extends ErrorGroup {
  recentEvents: ErrorEvent[];
  affectedUsers: number;
  affectedSessions: number;
}

// Issue Report types
export interface CapturedContext {
  currentRoute?: string;
  sessionId?: string;
  browser?: string;
  deviceType?: string;
  appVersion?: string;
  userAgent?: string;
  breadcrumbs?: unknown[];
  consoleErrors?: string[];
  networkErrors?: string[];
}

export interface IssueSubmission {
  title: string;
  description?: string;
  screenshotUrl?: string;
  annotationData?: Record<string, unknown>;
  selectedElement?: Record<string, unknown>;
  issueType?: 'bug' | 'suggestion' | 'question';
  severity?: 'critical' | 'high' | 'medium' | 'low';
  reportedByUserId?: string;
  reportedByEmail?: string;
  reportedByRole?: string;
  context?: CapturedContext;
  branchId?: string;
}

export interface IssueReport {
  id: string;
  source: string;
  groupHash: string | null;
  groupedCount: number | null;
  status: string;
  classification: string | null;
  severity: string;
  issueType: string | null;
  dismissReason: string | null;
  title: string;
  description: string | null;
  screenshotUrl: string | null;
  annotationData: Record<string, unknown> | null;
  selectedElement: Record<string, unknown> | null;
  context: CapturedContext | null;
  reportedByUserId: string | null;
  reportedByEmail: string | null;
  reportedByRole: string | null;
  analysisClassification: string | null;
  analysisSeverity: string | null;
  analysisConfidence: number | null;
  analysisRootCause: string | null;
  analysisProposedApproach: string | null;
  analysisAffectedFiles: unknown[] | null;
  analyzedAt: string | null;
  resolvedAt: string | null;
  resolvedBy: string | null;
  resolutionNotes: string | null;
  branchId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface IssueFilters {
  status?: string;
  severity?: string;
  classification?: string;
  search?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
}

export interface IssueUpdate {
  status?: string;
  severity?: string;
  classification?: string;
  dismissReason?: string;
  analysisClassification?: string;
  analysisSeverity?: string;
  analysisConfidence?: number;
  analysisRootCause?: string;
  analysisProposedApproach?: string;
  analysisAffectedFiles?: unknown[];
  analyzedAt?: string;
  resolvedAt?: string;
  resolvedBy?: string;
  resolutionNotes?: string;
}

export interface IssueStats {
  total: number;
  new: number;
  analyzing: number;
  needsReview: number;
  implementing: number;
  done: number;
}

export interface IssueListResponse {
  data: IssueReport[];
  meta: {
    total: number;
    limit: number;
    offset: number;
  };
  stats: IssueStats;
}

export interface IssueAnalysisData {
  classification: 'bug' | 'non_issue' | 'feature_request';
  severity: 'critical' | 'high' | 'medium' | 'low';
  confidence: number;
  rootCause: string;
  proposedApproach: string;
  affectedFiles?: string[];
  suggestedLabels?: string[];
}

export interface IssueAnalysisResponse {
  issue: IssueReport;
  analysis: IssueAnalysisData;
}

// Sync Verification types
export interface SyncTableCount {
  table: string;
  dbCount: number;
  sheetCount: number | null;
  sheetName: string | null;
  tabName: string | null;
  match: boolean;
  error?: string;
  /** Present when the server deliberately did not measure this table (sheetCount is null): the reason. */
  note?: string;
}

export interface SyncVerificationResult {
  results: SyncTableCount[];
  summary: {
    total: number;
    matches: number;
    mismatches: number;
    errors: number;
    /** Tables the server deliberately did not measure. Absent on older servers. */
    notMeasured?: number;
  };
  duration: number;
  verifiedAt: string;
}

// Sync Reconciliation types
export interface RecordWithName {
  id: string;
  name: string; // First preview column value (e.g., employee name, client name)
}

export interface SyncTableReconciliation {
  table: string;
  keyColumn: string;
  sheetKeyColumn: string;
  nameColumn: string; // Which column we're showing for names
  dbCount: number;
  sheetCount: number;
  inBoth: number;
  /** TRUE number of records only in the sheet / only in the DB. inSheetOnly / inDbOnly are samples cut to the requested limit. Absent on older servers (use the list lengths). */
  sheetOnlyCount?: number;
  dbOnlyCount?: number;
  inSheetOnly: RecordWithName[];
  inDbOnly: RecordWithName[];
  error?: string;
}

export interface SyncReconciliationResult {
  results: SyncTableReconciliation[];
  summary: {
    tables: number;
    totalInBoth: number;
    totalSheetOnly: number;
    totalDbOnly: number;
  };
  duration: number;
}
